import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {
  getPerformanceStatus,
  parseTimestamp,
  formatAreaName,
  bareZoneKey,
  stfZoneToMapName,
  matchesArea,
  LOD_LEVELS,
  calculateOptimalLOD,
  FILE_LIMIT_OPTIONS,
  DATE_RANGE_OPTIONS,
} from '../lib/heatmapUtils';
import useAreaData from '../hooks/useAreaData';
import useMetricConfig from '../hooks/useMetricConfig';
import useSTFTrends from '../hooks/useSTFTrends';
import { evaluatePoint, AVAILABLE_METRICS } from '../lib/evaluator';
import MultiSelect from './MultiSelect';

import PerformanceAnalysis from './PerformanceAnalysis';
import CompareMode from './CompareMode';
import './Heatmaps.css';

// ============================================================================
// Unity LiveLink bridge (WebSocket via localhost popup)
// ============================================================================
function useUnityBridge() {
  const [connected, setConnected] = useState(false);
  const bridgeRef = useRef(null);

  useEffect(() => {
    function handleMessage(e) {
      if (!e.data || !e.data.type) return;
      if (e.data.type === 'unity-bridge-connected') setConnected(true);
      if (e.data.type === 'unity-bridge-disconnected') setConnected(false);
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!bridgeRef.current) return;
    const interval = setInterval(() => {
      if (bridgeRef.current && bridgeRef.current.closed) {
        bridgeRef.current = null;
        setConnected(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [connected]);

  const openBridge = useCallback(() => {
    if (bridgeRef.current && !bridgeRef.current.closed) {
      bridgeRef.current.focus();
      return;
    }
    bridgeRef.current = window.open(
      'http://localhost:54322/bridge',
      'unity-livelink',
      'width=320,height=160,menubar=no,toolbar=no,status=no'
    );
  }, []);

  const jumpToPoint = useCallback((x, y, z, yawDegrees, zoneId) => {
    if (!bridgeRef.current || bridgeRef.current.closed) return false;
    bridgeRef.current.postMessage({
      type: 'JumpToPoint',
      deviceId: '',
      zoneId: zoneId || '',
      sceneName: '',
      x, y, z,
      yawDegrees: yawDegrees || 0,
    }, '*');
    return true;
  }, []);

  const openScene = useCallback((zoneId) => {
    if (!bridgeRef.current || bridgeRef.current.closed) return false;
    bridgeRef.current.postMessage({
      type: 'OpenScene',
      zoneId: zoneId || '',
    }, '*');
    return true;
  }, []);

  return { connected, openBridge, jumpToPoint, openScene };
}

function formatScanNumber(n) {
  if (n == null || n < 0) return '-';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 10_000) return (n / 1_000).toFixed(1) + 'K';
  return String(Math.round(n));
}

function AreaSection({ area, api, daysFilter, startDate = null, endDate = null, filters = {}, defaultExpanded = false, colorMetric = 'framerate', activeEvaluator = null, performanceSummary = null, branch = 'current_dev', onCLFilter = null, links = null, unity = null }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [showPoints, setShowPoints] = useState(true);
  const [showFrustums, setShowFrustums] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('none');
  const sectionRef = useRef(null);
  const viewerRef = useRef(null);
  const savedCameraStateRef = useRef(null);

  // All data loading (GCS, BigQuery LOD, STF, map config)
  const {
    loading, error, data, mapConfig,
    fileLimit, setFileLimit,
    loadData,
    currentLOD, lodProgress, lodCacheRef,
    handleLODChange,
    deviceRuns,
    showAWSLayer, setShowAWSLayer,
    showSTFLayer, setShowSTFLayer,
    stfScanPoints, stfLoading,
    stfSelectedScans, setStfSelectedScans,
    stfScanHistory,
    stfReports,
  } = useAreaData({ api, area, daysFilter, startDate, endDate, filters, dataSource: 'bigquery', expanded });

  // Trend rings: pick the most recent selected STF scan as the "current" for trend baselines
  const currentSTFTestRunId = useMemo(() => {
    if (!stfSelectedScans || stfSelectedScans.size === 0 || !stfScanHistory) return null;
    // stfScanHistory is sorted most-recent-first; pick the first one that's selected
    for (const scan of stfScanHistory) {
      if (stfSelectedScans.has(scan.test_run_id)) return scan.test_run_id;
    }
    return null;
  }, [stfSelectedScans, stfScanHistory]);

  const { trendData, trendLoading } = useSTFTrends(
    api,
    area.stfZone?.zone_name || null,
    currentSTFTestRunId,
    stfScanPoints,
    activeEvaluator
  );

  // Scroll into view when initially expanded via deep link
  useEffect(() => {
    if (defaultExpanded && sectionRef.current) {
      const timer = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleToggle = useCallback(() => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
  }, [expanded]);

  // Auto-select all device runs when they first load
  const prevDeviceRunsRef = useRef(null);
  useEffect(() => {
    if (deviceRuns && deviceRuns.length > 0 && prevDeviceRunsRef.current !== deviceRuns) {
      prevDeviceRunsRef.current = deviceRuns;
      setSelectedFiles(new Set(deviceRuns.map(r => r.test_run_id)));
    }
  }, [deviceRuns]);

  // Memoize filtered data to prevent infinite re-render loop when files are selected
  // Without this, the inline filter creates a new object every render, triggering useEffect cleanup
  const filteredData = useMemo(() => {
    if (!data && !stfScanPoints) return null;
    let points = [];
    // Include AWS/device data when layer is active
    if (showAWSLayer && data) {
      if (deviceRuns && deviceRuns.length > 0 && selectedFiles.size === 0) {
        // All device runs unchecked — show nothing
      } else {
        points = [...data.capturePoints];
        // At LOD 2 (full resolution), _source = TestRunId so per-run filtering works.
        // At LOD 0/1, _source is a grid key — show all if any runs are selected.
        if (selectedFiles.size > 0 && data._lodLevel === 2) {
          points = points.filter(p => selectedFiles.has(p._source));
        }
      }
    }
    // Merge STF scan points when layer is active
    if (showSTFLayer && stfScanPoints) {
      points = points.concat(stfScanPoints);
    }
    return {
      ...(data || { files: [], filesLoaded: 0, totalFiles: 0 }),
      capturePoints: points,
      totalPoints: points.length
    };
  }, [data, selectedFiles, deviceRuns, showAWSLayer, showSTFLayer, stfScanPoints]);

  // Get area performance summary (for collapsed view indicators)
  const areaPerfSummary = performanceSummary?.find(p => p.zoneName === area.name);

  // Stale indicator: show when no STF report exists or last activity > 24h ago
  const staleInfo = useMemo(() => {
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const stfTime = area.stfZone?.scanned_at
      ? new Date(area.stfZone.scanned_at.value || area.stfZone.scanned_at).getTime()
      : null;
    const awsTime = area.lastUpdated
      ? new Date(area.lastUpdated.value || area.lastUpdated).getTime()
      : null;
    const latestTime = Math.max(stfTime || 0, awsTime || 0);

    if (!stfTime && !awsTime) return { stale: true, title: 'No scan data' };
    if (!stfTime) return { stale: true, title: 'No STF report' };
    if (now - latestTime > DAY_MS) {
      const hoursAgo = Math.floor((now - latestTime) / (60 * 60 * 1000));
      const daysAgo = Math.floor(hoursAgo / 24);
      const label = daysAgo > 0 ? `${daysAgo}d ago` : `${hoursAgo}h ago`;
      return { stale: true, title: `Last data ${label}` };
    }
    return { stale: false };
  }, [area.stfZone, area.lastUpdated]);

  // Handle "View in Heatmap" click from performance analysis
  const handleViewInHeatmap = useCallback((cl) => {
    if (onCLFilter) {
      onCLFilter([String(cl)]);
    }
  }, [onCLFilter]);

  // Get worst-device performance for header display (worst avg and worst low may differ)
  const worstByAvg = areaPerfSummary?.devices?.[0]; // devices sorted by avg_fps ASC
  const worstByLow = areaPerfSummary?.devices?.length > 0
    ? areaPerfSummary.devices.reduce((w, d) => (d.lowFps < w.lowFps ? d : w))
    : null;

  return (
    <div ref={sectionRef} className={`area-section ${expanded ? 'expanded' : 'collapsed'}`}>
      <button className="area-header" onClick={handleToggle}>
        <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        <span className="area-name">{formatAreaName(area.name)}</span>
        {staleInfo.stale && (
          <span className="area-stale-badge" title={staleInfo.title}>⏳</span>
        )}
        {worstByAvg && worstByLow && (
          <span className="area-summary-stats"
            title={`Worst avg: ${worstByAvg.device} (${worstByAvg.quality}) — ${worstByAvg.avgFps}\nWorst low: ${worstByLow.device} (${worstByLow.quality}) — ${worstByLow.lowFps}`}>
            <span className="area-fps-label">Worst:</span>
            <span className={`area-fps-value ${worstByAvg.avgFps >= 45 ? 'good' : worstByAvg.avgFps >= 35 ? 'medium' : 'critical'}`}>
              {worstByAvg.avgFps} AVG
            </span>
            <span className={`area-fps-value ${worstByLow.lowFps >= 30 ? 'good' : worstByLow.lowFps >= 20 ? 'medium' : 'critical'}`}>
              {worstByLow.lowFps} LOW
            </span>
          </span>
        )}
        <span className="area-header-links" onClick={(e) => e.stopPropagation()}>
          {unity && (
            unity.connected ? (
              <button
                className="area-header-btn area-unity-btn unity-connected"
                onClick={() => unity.openScene(area.stfZone?.zone_name || area.zoneName || area.name)}
                title="Open scene in Unity Editor"
              >
                Open Scene
              </button>
            ) : (
              <button
                className="area-header-btn area-unity-btn unity-disconnected"
                onClick={() => unity.openBridge()}
                title="Connect to Unity Editor"
              >
                Connect Unity
              </button>
            )
          )}
          {links?.harvestHealth && (
            <a
              className="cross-link-pill"
              href={`${links.harvestHealth}/scorecard?days=${daysFilter || 7}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View FPS scorecard for all areas"
            >
              Scorecard ↗
            </a>
          )}
        </span>
      </button>

      {expanded && (
        <div className="area-content">
          {loading && (
            <div className="area-loading">
              <div className="loading-spinner"></div>
              <span>Loading capture data...</span>
            </div>
          )}

          {error && (
            <div className="area-error">
              <span>Failed to load: {error}</span>
              <button onClick={loadData}>Retry</button>
            </div>
          )}

          {(data || stfScanPoints) && (
            <div className="area-data">
              <div className="area-controls">
                {!area._stfOnly && (
                <div className="file-limit-control">
                  <label htmlFor={`file-limit-${area.name}`}>Load runs:</label>
                  <select
                    id={`file-limit-${area.name}`}
                    value={fileLimit}
                    onChange={(e) => setFileLimit(Number(e.target.value))}
                    disabled={compareMode}
                  >
                    {FILE_LIMIT_OPTIONS.map(n => (
                      <option key={n} value={n}>{n} most recent</option>
                    ))}
                  </select>
                </div>
                )}
                {!area._stfOnly && (
                  <button
                    className={`compare-mode-toggle ${compareMode ? 'active' : ''}`}
                    onClick={() => setCompareMode(!compareMode)}
                  >
                    {compareMode ? 'Exit Compare' : 'Compare CLs'}
                  </button>
                )}
                <div className="layer-checkboxes">
                  <label className="layer-checkbox">
                    <input type="checkbox" checked={showPoints} onChange={() => setShowPoints(v => !v)} />
                    <span>Points</span>
                  </label>
                  <label className="layer-checkbox">
                    <input type="checkbox" checked={showFrustums} onChange={() => setShowFrustums(v => !v)} />
                    <span>Frustums</span>
                  </label>
                  <label className="layer-checkbox">
                    <span>Trends:</span>
                    <select value={trendPeriod} onChange={(e) => setTrendPeriod(e.target.value)}>
                      <option value="none">None</option>
                      <option value="lastRun">vs Last Run</option>
                      <option value="lastWeek">vs Last Week</option>
                      <option value="lastMonth">vs Last Month</option>
                    </select>
                  </label>
                </div>
              </div>

              {/* Compare Mode - show split viewers */}
              {compareMode ? (
                <CompareMode
                  area={area}
                  api={api}
                  daysFilter={daysFilter}
                  startDate={startDate}
                  endDate={endDate}
                  colorMetric={colorMetric}
                  activeEvaluator={activeEvaluator}
                  mapConfig={mapConfig}
                  filters={filters}
                  onExit={() => setCompareMode(false)}
                  initialCameraState={savedCameraStateRef.current}
                />
              ) : (
                <>
                  <div className="viewer-container" ref={viewerRef}>
                    <HeatmapViewer
                      data={filteredData}
                      containerRef={viewerRef}
                      mapConfig={mapConfig}
                      selectedPoints={selectedPoints}
                      setSelectedPoints={setSelectedPoints}
                      colorMetric={colorMetric}
                      activeEvaluator={activeEvaluator}
                      trendData={trendData}
                      trendPeriod={trendPeriod}
                      currentLOD={currentLOD}
                      onLODChange={handleLODChange}
                      lodCacheRef={lodCacheRef}
                      lodProgress={lodProgress}
                      onCameraChange={(state) => { savedCameraStateRef.current = state; }}
                      initialCameraState={savedCameraStateRef.current}
                      showPoints={showPoints}
                      showFrustums={showFrustums}
                      unity={unity}
                      zoneId={area.stfZone?.zone_name || area.zoneName || area.name}
                      externalLoading={stfLoading || trendLoading}
                    />
                    {!mapConfig && (
                      <p className="no-map-note">
                        No map image available for this zone. Export one from Unity using the Map Export Tool.
                      </p>
                    )}
                  </div>

                  {/* Editor Scan Runs table (STF zones with scan data) */}
                  {stfScanHistory && stfScanHistory.length > 0 && (
                    <EditorScanTable
                      scans={stfScanHistory}
                      reports={stfReports}
                      selectedScans={stfSelectedScans}
                      showLayer={showSTFLayer}
                      onToggleLayer={() => setShowSTFLayer(v => !v)}
                      onToggleScan={(id) => {
                        setStfSelectedScans(prev => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                      }}
                      onSelectAll={(ids) => setStfSelectedScans(new Set(ids))}
                      onSelectNone={() => setStfSelectedScans(new Set())}
                      api={api}
                      colorMetric={colorMetric}
                      activeEvaluator={activeEvaluator}
                    />
                  )}

                  {/* Device Runs table (AWS / on-device data) */}
                  {deviceRuns && deviceRuns.length > 0 && (
                    <DeviceRunTable
                      runs={deviceRuns}
                      selectedFiles={selectedFiles}
                      showLayer={showAWSLayer}
                      onToggleLayer={() => setShowAWSLayer(v => !v)}
                      onToggleFile={(name) => {
                        setSelectedFiles(prev => {
                          const next = new Set(prev);
                          if (next.has(name)) next.delete(name);
                          else next.add(name);
                          return next;
                        });
                      }}
                      onSelectAll={(names) => setSelectedFiles(new Set(names))}
                      onSelectNone={() => setSelectedFiles(new Set())}
                      colorMetric={colorMetric}
                    />
                  )}

                  {/* Performance Analysis Section */}
                  <PerformanceAnalysis
                    area={area}
                    api={api}
                    daysFilter={daysFilter}
                    startDate={startDate}
                    endDate={endDate}
                    branch={branch}
                    filters={filters}
                    onViewInHeatmap={handleViewInHeatmap}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const RUNS_PER_PAGE = 20;

// Compute evaluator score from a scan/run summary row's average fields
function evalScoreFromSummary(row, evaluator) {
  if (!evaluator?.rules?.length) return null;
  // Map scan summary field names to evaluator metric keys
  const syntheticPoint = {
    triangles: row.avg_triangles,
    vertices: row.avg_vertices,
    drawCalls: row.avg_draw_calls,
    batches: row.avg_batches,
    visibleRenderers: row.avg_visible_renderers,
    candidateRenderers: row.avg_candidate_renderers,
    candidateTriangles: row.avg_candidate_triangles,
  };
  const { score } = evaluatePoint(syntheticPoint, evaluator);
  return score;
}

// Map colorMetric to the summary field name on scan/run objects and a short column label
function colorMetricColumn(colorMetric) {
  if (!colorMetric || colorMetric.startsWith('eval:')) return null;
  const map = {
    framerate:   { field: 'avg_fps',                label: 'Avg FPS' },
    gpuFps:      { field: 'avg_fps',                label: 'Avg FPS' },
    cpuFps:      { field: 'avg_fps',                label: 'Avg FPS' },
    drawCalls:   { field: 'avg_draw_calls',         label: 'Avg Draws' },
    batches:     { field: 'avg_batches',            label: 'Avg Batches' },
    triangles:   { field: 'avg_triangles',          label: 'Avg Tris' },
    vertices:    { field: 'avg_vertices',           label: 'Avg Verts' },
    visibleRenderers:          { field: 'avg_visible_renderers',          label: 'Avg Vis Renderers' },
    candidateRenderers:        { field: 'avg_candidate_renderers',        label: 'Avg Cand Renderers' },
    setPassCalls:              { field: 'avg_set_pass_calls',             label: 'Avg SetPass' },
    shadowCasterRenderers:     { field: 'avg_shadow_caster_renderers',    label: 'Avg Shadow Casters' },
    batchedDraws:              { field: 'avg_batched_draws',              label: 'Avg Batched' },
    unbatchedDraws:            { field: 'avg_unbatched_draws',            label: 'Avg Unbatched' },
  };
  return map[colorMetric] || null;
}

/**
 * Editor Scan table — STF scan runs with checkboxes, report links, and pagination.
 */
function EditorScanTable({ scans, reports, selectedScans, onToggleScan, onSelectAll, onSelectNone, api, showLayer, onToggleLayer, colorMetric, activeEvaluator }) {
  const [page, setPage] = useState(0);
  const [reportLoading, setReportLoading] = useState(false);

  const totalPages = Math.ceil(scans.length / RUNS_PER_PAGE);
  const pageScans = scans.slice(page * RUNS_PER_PAGE, (page + 1) * RUNS_PER_PAGE);

  const allOnPageSelected = pageScans.every(s => selectedScans.has(s.test_run_id));
  const someSelected = selectedScans.size > 0;

  // Build a lookup from report filename (stem) to report object.
  // Reports uploaded by the automated pipeline use TestRunId as the filename;
  // manual editor scans still use a local-time timestamp.
  const reportByName = useMemo(() => {
    if (!reports || reports.length === 0) return {};
    const map = {};
    for (const r of reports) {
      map[r.name] = r;
    }
    return map;
  }, [reports]);

  const findReport = useCallback((scan) => {
    if (!scan.test_run_id) return null;
    return reportByName[scan.test_run_id] || null;
  }, [reportByName]);

  const openReport = useCallback(async (reportPath) => {
    setReportLoading(true);
    try {
      const url = api.getSTFScanReportUrl(reportPath);
      const token = await api.getToken();
      const resp = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('[EditorScanTable] Failed to open report:', err);
      alert('Failed to open report: ' + err.message);
    } finally {
      setReportLoading(false);
    }
  }, [api]);

  const togglePageAll = () => {
    if (allOnPageSelected) {
      // Deselect this page's scans
      const pageIds = new Set(pageScans.map(s => s.test_run_id));
      const remaining = [...selectedScans].filter(id => !pageIds.has(id));
      if (remaining.length === 0) onSelectNone();
      else onSelectAll(remaining);
    } else {
      // Add this page's scans to selection
      const merged = new Set(selectedScans);
      pageScans.forEach(s => merged.add(s.test_run_id));
      onSelectAll([...merged]);
    }
  };

  return (
    <div className="run-table-section">
      <div className="run-table-header">
        <label className="run-table-layer-toggle">
          <input type="checkbox" checked={showLayer} onChange={onToggleLayer} />
          <h4>Editor Scans</h4>
        </label>
        <span className="run-table-info">
          {selectedScans.size} of {scans.length} selected
        </span>
      </div>
      <table className="run-table">
        <thead>
          <tr>
            <th className="run-table-check">
              <input type="checkbox" checked={allOnPageSelected} onChange={togglePageAll} title="Toggle all on this page" />
            </th>
            <th>Date</th>
            <th>CL</th>
            <th>Branch</th>
            <th>Points</th>
            <th>Avg Draws</th>
            <th>Avg Batches</th>
            <th>Avg Tris</th>
            {(() => { const col = colorMetricColumn(colorMetric); return col && !['avg_draw_calls','avg_batches','avg_triangles'].includes(col.field) ? <th className="run-table-metric-col">{col.label}</th> : null; })()}
            {activeEvaluator && <th className="run-table-metric-col">Score</th>}
            <th>Report</th>
          </tr>
        </thead>
        <tbody>
          {pageScans.map((scan, idx) => {
            const globalIdx = page * RUNS_PER_PAGE + idx;
            const d = scan.scanned_at ? new Date(scan.scanned_at.value || scan.scanned_at) : null;
            const dateStr = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-';
            const checked = selectedScans.has(scan.test_run_id);
            const report = findReport(scan);
            const prevScan = scans[globalIdx + 1];
            const metricCol = colorMetricColumn(colorMetric);
            const showMetricCol = metricCol && !['avg_draw_calls','avg_batches','avg_triangles'].includes(metricCol.field);
            const evalScore = activeEvaluator ? evalScoreFromSummary(scan, activeEvaluator) : null;
            const prevEvalScore = activeEvaluator && prevScan ? evalScoreFromSummary(prevScan, activeEvaluator) : null;

            return (
              <tr key={scan.test_run_id} className={`run-table-row ${checked ? 'run-table-row-selected' : ''}`}>
                <td className="run-table-check">
                  <input type="checkbox" checked={checked} onChange={() => onToggleScan(scan.test_run_id)} />
                </td>
                <td className="run-table-date">{dateStr}</td>
                <td>{scan.client_changelist || '-'}</td>
                <td>{scan.client_branch || '-'}</td>
                <td>{scan.point_count}</td>
                <td>
                  {formatScanNumber(scan.avg_draw_calls)}
                  {prevScan && <ScanDelta current={scan.avg_draw_calls} previous={prevScan.avg_draw_calls} />}
                </td>
                <td>
                  {formatScanNumber(scan.avg_batches)}
                  {prevScan && <ScanDelta current={scan.avg_batches} previous={prevScan.avg_batches} />}
                </td>
                <td>
                  {formatScanNumber(scan.avg_triangles)}
                  {prevScan && <ScanDelta current={scan.avg_triangles} previous={prevScan.avg_triangles} />}
                </td>
                {showMetricCol && (
                  <td className="run-table-metric-col">
                    {formatScanNumber(scan[metricCol.field])}
                    {prevScan && <ScanDelta current={scan[metricCol.field]} previous={prevScan[metricCol.field]} />}
                  </td>
                )}
                {activeEvaluator && (
                  <td className="run-table-metric-col">
                    {evalScore != null ? Math.round(evalScore * 100) + '%' : '-'}
                    {prevEvalScore != null && <ScanDelta current={evalScore} previous={prevEvalScore} />}
                  </td>
                )}
                <td className="run-table-report" onClick={e => e.stopPropagation()}>
                  {report ? (
                    <button className="scan-report-link" onClick={() => openReport(report.path)} disabled={reportLoading} title="Open full HTML report">
                      {reportLoading ? '...' : 'Report'}
                    </button>
                  ) : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="run-table-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>&laquo; Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next &raquo;</button>
        </div>
      )}
    </div>
  );
}

/**
 * Device Run table — AWS / on-device runs with checkboxes, pagination.
 * Uses BigQuery per-run metadata (not GCS file list).
 */
function DeviceRunTable({ runs, selectedFiles, onToggleFile, onSelectAll, onSelectNone, showLayer, onToggleLayer, colorMetric }) {
  const [page, setPage] = useState(0);

  // runs are already sorted newest first from the server
  const totalPages = Math.ceil(runs.length / RUNS_PER_PAGE);
  const pageRuns = runs.slice(page * RUNS_PER_PAGE, (page + 1) * RUNS_PER_PAGE);

  // "selected" means filtering to only these runs — empty set = show all
  const allOnPageFiltered = pageRuns.every(r => selectedFiles.has(r.test_run_id));
  const someFiltered = selectedFiles.size > 0;

  const togglePageAll = () => {
    if (someFiltered && allOnPageFiltered) {
      const pageIds = new Set(pageRuns.map(r => r.test_run_id));
      const remaining = [...selectedFiles].filter(n => !pageIds.has(n));
      if (remaining.length === 0) onSelectNone();
      else onSelectAll(remaining);
    } else if (someFiltered) {
      const merged = new Set(selectedFiles);
      pageRuns.forEach(r => merged.add(r.test_run_id));
      onSelectAll([...merged]);
    } else {
      onSelectAll(pageRuns.map(r => r.test_run_id));
    }
  };

  return (
    <div className="run-table-section">
      <div className="run-table-header">
        <label className="run-table-layer-toggle">
          <input type="checkbox" checked={showLayer} onChange={onToggleLayer} />
          <h4>Device Runs</h4>
        </label>
        <span className="run-table-info">
          {selectedFiles.size} of {runs.length} selected
        </span>
      </div>
      <table className="run-table">
        <thead>
          <tr>
            <th className="run-table-check">
              <input type="checkbox" checked={someFiltered && allOnPageFiltered} onChange={togglePageAll} title="Filter to this page" />
            </th>
            <th>Date</th>
            <th>Device</th>
            <th>Quality</th>
            <th>CL</th>
            <th>Branch</th>
            <th>Points</th>
            <th>Avg FPS</th>
            <th>Low FPS</th>
            {(() => { const col = colorMetricColumn(colorMetric); return col && !['avg_fps'].includes(col.field) && runs.some(r => r[col.field] != null) ? <th className="run-table-metric-col">{col.label}</th> : null; })()}
          </tr>
        </thead>
        <tbody>
          {pageRuns.map((run) => {
            const ts = run.timestamp ? new Date(run.timestamp) : null;
            const dateStr = ts
              ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                + ' ' + ts.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
              : '-';
            const isFiltered = selectedFiles.has(run.test_run_id);
            const metricCol = colorMetricColumn(colorMetric);
            const showMetricCol = metricCol && !['avg_fps'].includes(metricCol.field) && runs.some(r => r[metricCol.field] != null);

            return (
              <tr key={run.test_run_id} className={`run-table-row ${isFiltered ? 'run-table-row-selected' : ''}`}>
                <td className="run-table-check">
                  <input type="checkbox" checked={isFiltered} onChange={() => onToggleFile(run.test_run_id)} />
                </td>
                <td className="run-table-date">{dateStr}</td>
                <td>{run.device || 'Unknown'}</td>
                <td>{run.quality || '-'}</td>
                <td>{run.cl || '-'}</td>
                <td>{run.branch || '-'}</td>
                <td>{run.point_count ?? '-'}</td>
                <td>{run.avg_fps ?? '-'}</td>
                <td>{run.low_fps ?? '-'}</td>
                {showMetricCol && <td className="run-table-metric-col">{formatScanNumber(run[metricCol.field])}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="run-table-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>&laquo; Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next &raquo;</button>
        </div>
      )}
    </div>
  );
}

function ScanDelta({ current, previous }) {
  if (previous == null || current == null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return null;
  const cls = pct < 0 ? 'scan-delta-better' : 'scan-delta-worse';
  const sign = pct > 0 ? '+' : '';
  return <span className={`scan-delta ${cls}`}>{sign}{pct.toFixed(0)}%</span>;
}

/**
 * Three.js heatmap viewer component
 * Renders capture points in 3D space with orbit controls
 */
function HeatmapViewer({ data, containerRef, mapConfig, selectedPoints, setSelectedPoints, colorMetric = 'framerate', activeEvaluator = null, trendData = null, trendPeriod = 'none', currentLOD = 0, onLODChange = null, lodCacheRef = null, lodProgress = null, syncCameraState = null, viewerId = null, onCameraChange = null, initialCameraState = null, sharedBounds = null, showPoints = true, showFrustums = true, unity = null, zoneId = null, externalLoading = false }) {
  const canvasRef = useRef(null);
  const lastLODRef = useRef(currentLOD);  // Track LOD to detect changes
  const onLODChangeRef = useRef(onLODChange);  // Ref for callback to avoid stale closure
  const lodCacheRefCurrent = useRef(lodCacheRef);  // Keep lodCacheRef accessible in animate loop
  const syncCameraStateRef = useRef(syncCameraState);  // Ref for camera sync state
  const viewerIdRef = useRef(viewerId);  // Ref for viewer ID
  const lastSyncTimeRef = useRef(0);  // Track when we last synced from another viewer
  const onCameraChangeRef = useRef(onCameraChange);  // Ref for camera change callback
  const initialCameraStateRef = useRef(initialCameraState);  // Initial camera state to restore

  // Keep the callback ref up to date
  useEffect(() => {
    onLODChangeRef.current = onLODChange;
    lodCacheRefCurrent.current = lodCacheRef;
    syncCameraStateRef.current = syncCameraState;
    viewerIdRef.current = viewerId;
    onCameraChangeRef.current = onCameraChange;
  }, [onLODChange, lodCacheRef, syncCameraState, viewerId, onCameraChange]);

  const { getMetricFps, getMetricBadness, getMetricConfig } = useMetricConfig(colorMetric, activeEvaluator);

  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationIdRef = useRef(null);
  const viewerContainerRef = useRef(null);
  // Refs to preserve camera state across data updates (LOD changes)
  const savedCameraPosition = useRef(null);
  const savedControlsTarget = useRef(null);
  const savedFrustumSize = useRef(null);
  const savedCameraZoom = useRef(null);
  const [stats, setStats] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tooltip, setTooltip] = useState(null); // { x, y, fps, device }
  const nextPointIdRef = useRef(0); // For generating unique IDs
  const dotMeshesRef = useRef([]); // Store dot meshes for raycasting
  const fileMetadataRef = useRef({}); // Store file metadata for click handler
  const selectedWireframesRef = useRef({ group: null, geometry: null, material: null }); // For selected point wireframes
  const compositeMaterialRef = useRef(null); // For toggling frustum layer visibility
  const showPointsRef = useRef(showPoints);
  const showFrustumsRef = useRef(showFrustums);
  const trendPeriodRef = useRef(trendPeriod);
  const trendBarMeshesRef = useRef([]); // Store trend bar meshes for visibility toggling
  showPointsRef.current = showPoints;
  showFrustumsRef.current = showFrustums;
  trendPeriodRef.current = trendPeriod;

  // Handle fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!viewerContainerRef.current) return;

    if (!document.fullscreenElement) {
      viewerContainerRef.current.requestFullscreen().catch(err => {
        console.warn('Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Trigger resize after layout updates
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const hasPoints = data?.capturePoints && data.capturePoints.length > 0;
    if (!hasPoints && !mapConfig) return;
    if (!canvasRef.current) return;

    const points = hasPoints ? data.capturePoints : [];
    const canvas = canvasRef.current;

    // Calculate stats from capture points using selected metric (exclude null/undefined)
    const metricConfig = getMetricConfig();
    const metricValues = points.map(p => getMetricFps(p)).filter(f => f != null && f > 0);
    const rawValues = metricConfig.getValue ? points.map(p => metricConfig.getValue(p)).filter(v => v != null) : [];
    let minFps = 0, maxFps = 60;

    if (metricValues.length > 0) {
      const avgFps = metricValues.reduce((a, b) => a + b, 0) / metricValues.length;
      minFps = metricValues.reduce((a, b) => a < b ? a : b);
      maxFps = metricValues.reduce((a, b) => a > b ? a : b);

      // For evaluators, show raw score stats as percentages; for others, show FPS-scale values
      const useRaw = colorMetric?.startsWith('eval:') && rawValues.length > 0;
      const avgRaw = useRaw ? rawValues.reduce((a, b) => a + b, 0) / rawValues.length : null;
      const minRaw = useRaw ? rawValues.reduce((a, b) => a < b ? a : b) : null;
      const maxRaw = useRaw ? rawValues.reduce((a, b) => a > b ? a : b) : null;

      setStats({
        avgFps: useRaw ? metricConfig.format(avgRaw) : avgFps.toFixed(1),
        minFps: useRaw ? metricConfig.format(minRaw) : minFps.toFixed(1),
        maxFps: useRaw ? metricConfig.format(maxRaw) : maxFps.toFixed(1),
        pointCount: points.length,
        totalSamples: data?._totalSamples || null,
        cellSize: data?._cellSize || null,
        metricLabel: useRaw ? metricConfig.label : null
      });
    } else {
      setStats({ avgFps: '-', minFps: '-', maxFps: '-', pointCount: 0, totalSamples: null, cellSize: null, metricLabel: null });
    }

    // Find spatial bounds - use sharedBounds if provided (for CompareMode)
    let bounds;
    if (sharedBounds) {
      // Use shared bounds from CompareMode so both viewers have the same spatial reference
      bounds = { ...sharedBounds };
      console.log(`[HeatmapViewer] Using shared bounds from CompareMode`);
    } else if (points.length > 0) {
      // Compute bounds from points with IQR outlier filtering
      // to avoid distant outlier points (e.g., from loading screens) zooming out the view
      const xVals = [];
      const zVals = [];
      for (const p of points) {
        if (p.position) {
          xVals.push(-p.position.x);  // Negate for flipped coordinate system
          zVals.push(p.position.z);
        }
      }

      const filterOutliers = (values) => {
        if (values.length < 4) return { min: Math.min(...values), max: Math.max(...values) };
        const sorted = [...values].sort((a, b) => a - b);
        // Use 5th-95th percentile to frame the main data cluster
        const lo = sorted[Math.floor(sorted.length * 0.05)];
        const hi = sorted[Math.floor(sorted.length * 0.95)];
        const span = hi - lo || 1;
        // 20% padding around the data
        return { min: lo - span * 0.2, max: hi + span * 0.2 };
      };

      const xRange = filterOutliers(xVals);
      const zRange = filterOutliers(zVals);

      bounds = {
        minX: xRange.min, maxX: xRange.max,
        minY: Infinity, maxY: -Infinity,
        minZ: zRange.min, maxZ: zRange.max
      };

      // Y bounds from all points (no outlier filtering needed for height)
      for (const p of points) {
        if (p.position) {
          bounds.minY = Math.min(bounds.minY, p.position.y);
          bounds.maxY = Math.max(bounds.maxY, p.position.y);
        }
      }
      if (!isFinite(bounds.minY)) { bounds.minY = 0; bounds.maxY = 100; }

      // Expand bounds if they're too small (e.g., single point or tightly clustered data)
      const minBoundsSize = 20;
      if (bounds.maxX - bounds.minX < minBoundsSize) {
        const midX = (bounds.minX + bounds.maxX) / 2;
        bounds.minX = midX - minBoundsSize / 2;
        bounds.maxX = midX + minBoundsSize / 2;
      }
      if (bounds.maxZ - bounds.minZ < minBoundsSize) {
        const midZ = (bounds.minZ + bounds.maxZ) / 2;
        bounds.minZ = midZ - minBoundsSize / 2;
        bounds.maxZ = midZ + minBoundsSize / 2;
      }
    } else if (mapConfig) {
      // No points but we have a map — use map extents as bounds (negate X for flipped coords)
      bounds = {
        minX: -mapConfig.maxX, maxX: -mapConfig.minX,
        minY: 0, maxY: 100,
        minZ: mapConfig.minZ, maxZ: mapConfig.maxZ
      };
    }

    // Calculate center and size for camera positioning
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;
    const rangeX = bounds.maxX - bounds.minX || 1;
    const rangeY = bounds.maxY - bounds.minY || 1;
    const rangeZ = bounds.maxZ - bounds.minZ || 1;
    // For top-down view, use only X/Z extent for camera framing (Y is height, irrelevant for zoom)
    const mapRange = Math.max(rangeX, rangeZ);
    // maxRange still includes Y for things like far plane and grid sizing
    const maxRange = Math.max(rangeX, rangeY, rangeZ, 10);

    console.log(`[HeatmapViewer] Bounds: X[${bounds.minX.toFixed(1)}, ${bounds.maxX.toFixed(1)}] Y[${bounds.minY.toFixed(1)}, ${bounds.maxY.toFixed(1)}] Z[${bounds.minZ.toFixed(1)}, ${bounds.maxZ.toFixed(1)}]`);
    console.log(`[HeatmapViewer] maxRange=${maxRange.toFixed(1)}, points=${points.length}`);

    // Set up Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Create a container group rotated 180° around Y for correct map orientation
    // This avoids setting camera.up which breaks OrbitControls
    const sceneContent = new THREE.Group();
    sceneContent.rotation.y = Math.PI;
    scene.add(sceneContent);

    // Camera (orthographic for top-down map view)
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    const aspect = width / height;
    // Priority: initialCameraState (from compare mode toggle) > saved state > default
    const initState = initialCameraStateRef.current;
    const viewSize = initState?.frustumSize || savedFrustumSize.current || Math.max(10, mapRange * 0.6);

    const camera = new THREE.OrthographicCamera(
      -viewSize * aspect, viewSize * aspect,  // left, right
      viewSize, -viewSize,                     // top, bottom
      0.1, maxRange * 10                       // near, far
    );
    camera.frustumSize = viewSize;  // Store for resize handling

    // Restore saved camera position or use default (top-down view)
    // Note: Scene content is rotated 180° around Y, so we negate X and Z for world coordinates
    const worldCenterX = -centerX;
    const worldCenterZ = -centerZ;
    // Base camera height calculated from data bounds - used for consistent dot scaling
    const minCameraHeight = 100;
    const baseCameraHeight = Math.max(minCameraHeight, centerY + maxRange * 1.2);
    if (initState?.position) {
      camera.position.set(initState.position.x, initState.position.y, initState.position.z);
    } else if (savedCameraPosition.current) {
      camera.position.copy(savedCameraPosition.current);
    } else {
      camera.position.set(worldCenterX, baseCameraHeight, worldCenterZ);
    }
    // Restore saved zoom level (scroll wheel zoom)
    const initialZoom = initState?.zoom || savedCameraZoom.current;
    if (initialZoom) {
      camera.zoom = initialZoom;
      camera.updateProjectionMatrix();
    }
    camera.lookAt(worldCenterX, bounds.minY, worldCenterZ);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    // Orbit controls - configured for top-down map view
    const controls = new OrbitControls(camera, canvas);

    // Restore saved orbit target or set to center of map at ground level (using world coordinates)
    if (initState?.target) {
      controls.target.set(initState.target.x, initState.target.y, initState.target.z);
    } else if (savedControlsTarget.current) {
      controls.target.copy(savedControlsTarget.current);
    } else {
      controls.target.set(worldCenterX, bounds.minY, worldCenterZ);
    }

    // Lock to top-down view (only allow Y-axis rotation/spin)
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = 0;

    // Swap mouse buttons: left = pan, right = rotate/spin
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE
    };

    // Enable zoom via scroll (will be handled for orthographic below)
    controls.enableZoom = true;
    controls.enableDamping = false;
    controls.enablePan = true;
    controls.screenSpacePanning = true;  // Pan parallel to screen
    controls.update();

    // Set consistent screen orientation after OrbitControls setup
    camera.up.set(0, 0, -1);
    camera.lookAt(controls.target);

    controlsRef.current = controls;

    // WASD fly controls
    const moveSpeed = mapRange * 0.01;  // Base movement speed relative to horizontal scene size
    const keys = { w: false, a: false, s: false, d: false, q: false, e: false, shift: false };
    const moveDirection = new THREE.Vector3();
    const cameraDirection = new THREE.Vector3();
    const cameraRight = new THREE.Vector3();

    const onKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key in keys) keys[key] = true;
      if (e.shiftKey) keys.shift = true;
    };
    const onKeyUp = (e) => {
      const key = e.key.toLowerCase();
      if (key in keys) keys[key] = false;
      if (!e.shiftKey) keys.shift = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    // Store cleanup refs
    const keyCleanup = { onKeyDown, onKeyUp };

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    sceneContent.add(ambientLight);

    // Add directional light
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(1, 2, 1);
    sceneContent.add(dirLight);

    // Create ground grid for reference
    const gridSize = mapRange * 1.5;
    const gridDivisions = 20;
    const gridHelper = new THREE.GridHelper(gridSize, gridDivisions, 0x444466, 0x333355);
    gridHelper.position.set(centerX, bounds.minY - 0.1, centerZ);
    sceneContent.add(gridHelper);

    // Load 2D map image if provided
    if (mapConfig) {
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        mapConfig.imageUrl,
        (texture) => {
          const mapY = mapConfig.y ?? bounds.minY - 0.05;

          // Use ShapeGeometry with explicit vertex positions to guarantee corners
          // Negate X coordinates to flip map orientation
          const planeGeo = new THREE.BufferGeometry();
          const vertices = new Float32Array([
            // Triangle 1 (X negated for correct orientation)
            -mapConfig.minX, mapY, mapConfig.minZ,
            -mapConfig.maxX, mapY, mapConfig.minZ,
            -mapConfig.maxX, mapY, mapConfig.maxZ,
            // Triangle 2
            -mapConfig.minX, mapY, mapConfig.minZ,
            -mapConfig.maxX, mapY, mapConfig.maxZ,
            -mapConfig.minX, mapY, mapConfig.maxZ,
          ]);
          const uvs = new Float32Array([
            // Triangle 1 - standard UV mapping
            0, 0,
            1, 0,
            1, 1,
            // Triangle 2
            0, 0,
            1, 1,
            0, 1,
          ]);
          planeGeo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
          planeGeo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));

          const planeMat = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthWrite: false,
          });
          const plane = new THREE.Mesh(planeGeo, planeMat);
          plane.renderOrder = 0;  // Render map first (behind frustums)
          plane.userData.isMapPlane = true;
          sceneContent.add(plane);
        },
        undefined,
        (error) => {
          console.warn('[Heatmap] Failed to load map:', error.message);
        }
      );
    }

    // FPS color thresholds
    // 45+ = Green, 30 = Yellow, 25 = Red, 20- = Dark Red, null = Gray (no data)
    const getColorFromFPS = (framerate) => {
      let r, g, b;
      if (framerate == null) {
        // White - no data available
        r = 1; g = 1; b = 1;
      } else if (framerate >= 45) {
        // Green
        r = 0; g = 1; b = 0;
      } else if (framerate >= 30) {
        // Yellow to Green (30-45)
        const t = (framerate - 30) / 15;
        r = 1 - t;
        g = 1;
        b = 0;
      } else if (framerate >= 25) {
        // Red to Yellow (25-30)
        const t = (framerate - 25) / 5;
        r = 1;
        g = t;
        b = 0;
      } else if (framerate >= 20) {
        // Dark Red to Red (20-25)
        const t = (framerate - 20) / 5;
        r = 0.5 + 0.5 * t;
        g = 0;
        b = 0;
      } else {
        // Dark Red (below 20)
        r = 0.5; g = 0; b = 0;
      }
      return new THREE.Color(r, g, b);
    };


    // Create view frustums as solid cones
    const frustumSize = Math.max(0.3, Math.min(2, mapRange / 50));
    const frustumLength = frustumSize * 20;  // 10x longer for better visibility

    // Use actual camera FOV from capture data, default to 60 degrees
    const fovDegrees = data.cameraFieldOfView || 60;
    const halfAngleRad = (fovDegrees * Math.PI / 180) / 2;
    const frustumBaseRadius = frustumLength * Math.tan(halfAngleRad);

    // Create cone geometry pointing forward (-Z direction)
    const coneGeometry = new THREE.ConeGeometry(frustumBaseRadius, frustumLength, 4);
    coneGeometry.rotateY(Math.PI / 4);  // Rotate 45° so edges (not corners) align with axes
    coneGeometry.rotateX(Math.PI / 2);
    coneGeometry.translate(0, 0, -frustumLength / 2);

    // Create wireframe highlight for hovered frustum
    const wireframeGeometry = new THREE.ConeGeometry(frustumBaseRadius, frustumLength, 4);
    wireframeGeometry.rotateY(Math.PI / 4);  // Rotate 45° so edges (not corners) align with axes
    wireframeGeometry.rotateX(Math.PI / 2);
    wireframeGeometry.translate(0, 0, -frustumLength / 2);
    const wireframeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      wireframe: true,
      transparent: true,
      opacity: 0.9
    });
    const wireframeMesh = new THREE.Mesh(wireframeGeometry, wireframeMaterial);
    wireframeMesh.visible = false;
    wireframeMesh.renderOrder = 100;
    sceneContent.add(wireframeMesh);

    // Create group for selected point wireframes
    const selectedWireframesGroup = new THREE.Group();
    selectedWireframesGroup.renderOrder = 99;
    sceneContent.add(selectedWireframesGroup);
    // Store refs for updating wireframes when selectedPoints changes
    selectedWireframesRef.current = {
      group: selectedWireframesGroup,
      geometry: wireframeGeometry,
      material: new THREE.MeshBasicMaterial({
        color: 0x00ffff,  // Cyan for selected points
        wireframe: true,
        transparent: true,
        opacity: 0.8
      })
    };

    // Create separate scene for frustums - will be rendered to a texture then composited
    const frustumScene = new THREE.Scene();
    // Create a container group rotated 180° around Y to match main scene orientation
    const frustumContent = new THREE.Group();
    frustumContent.rotation.y = Math.PI;
    frustumScene.add(frustumContent);

    // Filter for valid position coordinates (finite numbers)
    const validPoints = points.filter(p =>
      p.position &&
      Number.isFinite(p.position.x) &&
      Number.isFinite(p.position.y) &&
      Number.isFinite(p.position.z)
    );

    const tempMatrix = new THREE.Matrix4();
    const tempQuaternion = new THREE.Quaternion();
    const tempPosition = new THREE.Vector3();
    const tempScale = new THREE.Vector3(1, 1, 1);

    // Validate quaternion has finite values and is normalized (or close to it)
    const isValidQuaternion = (q) => {
      if (!q) return false;
      const { x, y, z, w } = q;
      // Check all components are finite numbers
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z) || !Number.isFinite(w)) {
        return false;
      }
      // Check quaternion is normalized (length should be ~1)
      const lengthSq = x * x + y * y + z * z + w * w;
      return lengthSq > 0.5 && lengthSq < 1.5;  // Allow some tolerance
    };

    const setupInstancedMesh = (pts, material) => {
      if (pts.length === 0) return null;
      const mesh = new THREE.InstancedMesh(coneGeometry, material, pts.length);
      mesh.frustumCulled = false;
      let invalidQuatCount = 0;
      pts.forEach((p, i) => {
        // Negate X to match flipped map orientation
        tempPosition.set(-p.position.x, p.position.y, p.position.z);
        if (isValidQuaternion(p.rotation)) {
          // Use rotation directly - scene group rotation handles orientation
          tempQuaternion.set(p.rotation.x, p.rotation.y, p.rotation.z, p.rotation.w);
          tempQuaternion.normalize();  // Ensure normalized
        } else {
          invalidQuatCount++;
          if (invalidQuatCount <= 3) {
            console.warn('[Heatmap] Invalid quaternion at point', i, ':', p.rotation);
          }
          tempQuaternion.identity();
        }
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        mesh.setMatrixAt(i, tempMatrix);
        // Store badness in R, 1.0 in G (for averaging in composite shader)
        const badness = getMetricBadness(p);
        mesh.setColorAt(i, new THREE.Color(badness, 1.0, 0));
      });
      if (invalidQuatCount > 0) {
        console.warn(`[Heatmap] ${invalidQuatCount}/${pts.length} points had invalid quaternions`);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      return mesh;
    };

    // Accumulation material: writes (badness * fade, fade, 0) per fragment
    // Uses onBeforeCompile to inject distance-based fade into MeshBasicMaterial
    // (preserves Three.js instancing support while adding custom behavior)
    const frustumAccumMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1.0,
      depthTest: false,
      depthWrite: false,
      side: THREE.FrontSide,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
    });
    frustumAccumMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.frustumLength = { value: frustumLength };
      // Add varying for normalized distance along cone axis
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
        varying float vNormDist;
        uniform float frustumLength;`
      );
      // Compute normalized distance from local Z position
      // After geometry transforms: z=0 is apex (camera), z=-frustumLength is base (tip)
      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vNormDist = clamp(-position.z / frustumLength, 0.0, 1.0);`
      );
      // Apply fade: solid first half, linear fade to 0 in second half
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
        varying float vNormDist;`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <output_fragment>',
        `#include <output_fragment>
        float fade = vNormDist < 0.5 ? 1.0 : 1.0 - (vNormDist - 0.5) * 2.0;
        // Weight both badness (R) and count (G) by fade for correct weighted average
        gl_FragColor.r *= fade;
        gl_FragColor.g *= fade;`
      );
    };
    // Force unique shader cache key so onBeforeCompile is applied
    frustumAccumMaterial.customProgramCacheKey = () => 'frustumFade';

    // Single instanced mesh for all valid frustums (no per-tier splitting needed)
    const frustumMesh = setupInstancedMesh(validPoints, frustumAccumMaterial);
    if (frustumMesh) {
      frustumMesh.renderOrder = 0;
      frustumContent.add(frustumMesh);
    }

    // FPS tier groups - still needed for dot rendering (size/color per tier)
    const fpsGroups = {
      undefined: validPoints.filter(p => getMetricFps(p) == null),
      excellent: validPoints.filter(p => { const fps = getMetricFps(p); return fps != null && fps >= 45; }),
      good: validPoints.filter(p => { const fps = getMetricFps(p); return fps != null && fps >= 30 && fps < 45; }),
      warning: validPoints.filter(p => { const fps = getMetricFps(p); return fps != null && fps >= 25 && fps < 30; }),
      poor: validPoints.filter(p => { const fps = getMetricFps(p); return fps != null && fps >= 20 && fps < 25; }),
      critical: validPoints.filter(p => { const fps = getMetricFps(p); return fps != null && fps < 20; }),
    };
    const tiers = ['undefined', 'excellent', 'good', 'warning', 'poor', 'critical'];

    // Add small dots (or wedges for STF data) at each point's position
    // Scale sizes relative to horizontal scene extent (mapRange)
    const dotBaseSize = mapRange * 0.004;
    // Minimum scale factor to ensure dots/wedges never shrink below 0.5 units diameter
    const minDotScaleFactor = 0.25 / dotBaseSize;
    const dotMeshes = []; // Store for raycasting
    trendBarMeshesRef.current = []; // Reset trend bars on scene rebuild

    // Check if data is STF scan (has yaw data) — use wedge rendering
    const hasSTFData = validPoints.some(p => p._isSTFScan && p._yawDegrees != null);

    if (hasSTFData) {
      // --- Wedge (pie slice) rendering for STF scan data ---
      // Group points by grid position (same x,y,z = same scan point, different yaws)
      const posKeyMap = new Map(); // "x,y,z" -> [point, ...]
      for (const p of validPoints) {
        if (!p._isSTFScan || p._yawDegrees == null) continue;
        const key = `${p.position.x.toFixed(1)},${p.position.y.toFixed(1)},${p.position.z.toFixed(1)}`;
        if (!posKeyMap.has(key)) posKeyMap.set(key, []);
        posKeyMap.get(key).push(p);
      }

      // Create wedge geometry: flat circle sector on XZ plane, 45° arc (1/8 of circle)
      // The wedge spans from -22.5° to +22.5° around the Z axis (forward)
      const wedgeRadius = dotBaseSize * 3.0;
      const wedgeSegments = 6;
      const wedgeAngle = Math.PI / 4; // 45°
      const wedgeShape = new THREE.Shape();
      wedgeShape.moveTo(0, 0);
      for (let i = 0; i <= wedgeSegments; i++) {
        const a = -wedgeAngle / 2 + (wedgeAngle * i / wedgeSegments);
        wedgeShape.lineTo(Math.sin(a) * wedgeRadius, Math.cos(a) * wedgeRadius);
      }
      wedgeShape.lineTo(0, 0);
      const wedgeGeometry = new THREE.ShapeGeometry(wedgeShape);
      // Rotate so wedge lies flat on XZ plane (Shape is on XY, rotate -90° around X)
      wedgeGeometry.rotateX(-Math.PI / 2);

      // Count total wedge instances
      let totalWedges = 0;
      for (const group of posKeyMap.values()) totalWedges += group.length;

      // Also collect non-STF points for regular dot rendering
      const nonSTFPoints = validPoints.filter(p => !p._isSTFScan || p._yawDegrees == null);

      if (totalWedges > 0) {
        const wedgeMaterial = new THREE.MeshBasicMaterial({
          vertexColors: false,
          transparent: true,
          opacity: 0.85,
          side: THREE.DoubleSide,
          depthTest: false,
          depthWrite: false,
        });

        const wedgeMesh = new THREE.InstancedMesh(wedgeGeometry, wedgeMaterial, totalWedges);
        wedgeMesh.frustumCulled = false;
        const wedgePointData = []; // parallel array for raycasting

        const wedgeQuat = new THREE.Quaternion();
        const yAxis = new THREE.Vector3(0, 1, 0);
        let idx = 0;

        for (const group of posKeyMap.values()) {
          for (const p of group) {
            // Position: negate X for flipped coordinate system
            tempPosition.set(-p.position.x, p.position.y, p.position.z);
            // Rotate wedge by yaw so each slice faces its compass direction
            const yawRad = (p._yawDegrees || 0) * Math.PI / 180;
            wedgeQuat.setFromAxisAngle(yAxis, yawRad);
            tempScale.set(1, 1, 1);
            tempMatrix.compose(tempPosition, wedgeQuat, tempScale);
            wedgeMesh.setMatrixAt(idx, tempMatrix);
            wedgeMesh.setColorAt(idx, getColorFromFPS(getMetricFps(p)));
            wedgePointData.push(p);
            idx++;
          }
        }

        wedgeMesh.instanceMatrix.needsUpdate = true;
        if (wedgeMesh.instanceColor) wedgeMesh.instanceColor.needsUpdate = true;
        wedgeMesh.renderOrder = 15;
        wedgeMesh.userData.points = wedgePointData;
        sceneContent.add(wedgeMesh);
        dotMeshes.push(wedgeMesh);

        // Trend bars: per-yaw triangles showing regression/improvement.
        // Built as InstancedMesh with ShapeGeometry (same approach as wedges) so the
        // animate loop's dotScaleFactor scaling applies identically.
        const selectedPeriod = trendPeriodRef.current;
        if (trendData && trendData.size > 0 && selectedPeriod !== 'none') {
          const periodLabels = { lastRun: 'vs last run', lastWeek: 'vs last week', lastMonth: 'vs last month' };
          const barLength = wedgeRadius * 0.7;
          const barHalfWidth = wedgeRadius * 0.12;

          // Collect regression and improvement bars separately
          const regressions = [];
          const improvements = [];
          for (const [posKey, group] of posKeyMap) {
            const trend = trendData.get(posKey);
            const periodData = trend?.[selectedPeriod];
            if (!periodData) continue;

            for (const p of group) {
              const yaw = p._yawDegrees ?? 0;
              const yawTrend = periodData.get ? periodData.get(yaw) : null;
              if (!yawTrend || Math.abs(yawTrend.delta) < 0.05) continue;
              const pointData = {
                ...p,
                _isTrendRing: true,
                _trendPeriod: selectedPeriod,
                _trendPeriodLabel: periodLabels[selectedPeriod] || selectedPeriod,
                _trendDelta: yawTrend.delta,
                _trendCurrentScore: yawTrend.currentScore,
                _trendBaselineScore: yawTrend.baselineScore,
                _trendBaselineDate: yawTrend.scannedAt,
              };
              if (yawTrend.delta > 0) {
                regressions.push(pointData);
              } else {
                improvements.push(pointData);
              }
            }
          }

          // Regression triangle: tip points outward (in camera direction, toward the
          // source of the regression), base at wedge edge. Multiple regressions from
          // different scan points converge their arrows on the problematic area.
          const regShape = new THREE.Shape();
          regShape.moveTo(0, wedgeRadius + barLength); // tip (outward)
          regShape.lineTo(-barHalfWidth, wedgeRadius); // base left (at wedge edge)
          regShape.lineTo(barHalfWidth, wedgeRadius);  // base right (at wedge edge)
          regShape.lineTo(0, wedgeRadius + barLength);
          const regGeo = new THREE.ShapeGeometry(regShape);
          regGeo.rotateX(-Math.PI / 2);

          // Improvement triangle: tip points inward (toward center), base at wedge edge.
          // Visual contrast with regressions: black arrows out = worse, white arrows in = better.
          const impShape = new THREE.Shape();
          impShape.moveTo(0, wedgeRadius - barLength); // tip (inward)
          impShape.lineTo(-barHalfWidth, wedgeRadius); // base left (at wedge edge)
          impShape.lineTo(barHalfWidth, wedgeRadius);  // base right (at wedge edge)
          impShape.lineTo(0, wedgeRadius - barLength);
          const impGeo = new THREE.ShapeGeometry(impShape);
          impGeo.rotateX(-Math.PI / 2);

          // Create InstancedMesh for each type and push to dotMeshes
          // so the animate loop applies the same dotScaleFactor scaling
          const createBarMesh = (geometry, barPoints, color) => {
            if (barPoints.length === 0) return;
            const mat = new THREE.MeshBasicMaterial({
              color,
              transparent: true,
              opacity: 0.9,
              side: THREE.DoubleSide,
              depthTest: false,
              depthWrite: false,
            });
            const mesh = new THREE.InstancedMesh(geometry, mat, barPoints.length);
            mesh.frustumCulled = false;
            mesh.renderOrder = 20;
            mesh.userData.points = barPoints;
            mesh.userData._isTrendBar = true;

            const barQuat = new THREE.Quaternion();
            barPoints.forEach((p, i) => {
              tempPosition.set(-p.position.x, p.position.y, p.position.z);
              const yawRad = (p._yawDegrees || 0) * Math.PI / 180;
              barQuat.setFromAxisAngle(yAxis, yawRad);
              tempScale.set(1, 1, 1);
              tempMatrix.compose(tempPosition, barQuat, tempScale);
              mesh.setMatrixAt(i, tempMatrix);
            });
            mesh.instanceMatrix.needsUpdate = true;

            mesh.visible = trendPeriodRef.current !== 'none';
            sceneContent.add(mesh);
            // Push to dotMeshes so animate loop applies dotScaleFactor scaling
            dotMeshes.push(mesh);
            trendBarMeshesRef.current.push(mesh);
          };

          createBarMesh(regGeo, regressions, 0x000000);
          createBarMesh(impGeo, improvements, 0xffffff);
        }
      }

      // Render non-STF points as regular dots (if any mixed data)
      if (nonSTFPoints.length > 0) {
        const dotGeometry = new THREE.SphereGeometry(dotBaseSize * 1.5, 8, 6);
        const dotMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.9 });
        const mesh = new THREE.InstancedMesh(dotGeometry, dotMaterial, nonSTFPoints.length);
        mesh.frustumCulled = false;
        mesh.userData.points = nonSTFPoints;
        const dotQuat = new THREE.Quaternion();
        nonSTFPoints.forEach((p, i) => {
          tempPosition.set(-p.position.x, p.position.y, p.position.z);
          tempMatrix.compose(tempPosition, dotQuat, tempScale);
          mesh.setMatrixAt(i, tempMatrix);
          mesh.setColorAt(i, getColorFromFPS(getMetricFps(p)));
        });
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        mesh.renderOrder = 12;
        sceneContent.add(mesh);
        dotMeshes.push(mesh);
      }
    } else {
      // --- Standard dot rendering for non-STF data ---
      const dotGeometries = {
        undefined: new THREE.SphereGeometry(dotBaseSize * 1.0, 8, 6),
        excellent: new THREE.SphereGeometry(dotBaseSize * 1.0, 8, 6),
        good: new THREE.SphereGeometry(dotBaseSize * 1.3, 8, 6),
        warning: new THREE.SphereGeometry(dotBaseSize * 2.0, 8, 6),
        poor: new THREE.SphereGeometry(dotBaseSize * 2.7, 8, 6),
        critical: new THREE.SphereGeometry(dotBaseSize * 3.3, 8, 6),
      };

      const dotMaterials = {
        undefined: new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 }),
        excellent: new THREE.MeshBasicMaterial({ color: 0x00ff00, transparent: true, opacity: 0.9 }),
        good: new THREE.MeshBasicMaterial({ color: 0x80ff00, transparent: true, opacity: 0.9 }),
        warning: new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.9 }),
        poor: new THREE.MeshBasicMaterial({ color: 0xff4000, transparent: true, opacity: 0.9 }),
        critical: new THREE.MeshBasicMaterial({ color: 0x800000, transparent: true, opacity: 0.9 }),
      };

      const dotQuat = new THREE.Quaternion();
      const dotScale = new THREE.Vector3(1, 1, 1);
      const tiersReversed = [...tiers].reverse();
      tiersReversed.forEach((tier, index) => {
        const pts = fpsGroups[tier];
        if (pts.length === 0) return;
        const mesh = new THREE.InstancedMesh(dotGeometries[tier], dotMaterials[tier], pts.length);
        mesh.frustumCulled = false;
        mesh.userData.points = pts;
        pts.forEach((p, i) => {
          tempPosition.set(-p.position.x, p.position.y, p.position.z);
          tempMatrix.compose(tempPosition, dotQuat, dotScale);
          mesh.setMatrixAt(i, tempMatrix);
        });
        mesh.instanceMatrix.needsUpdate = true;
        mesh.renderOrder = 10 + (tiersReversed.length - index);
        sceneContent.add(mesh);
        dotMeshes.push(mesh);
      });
    }

    // Respect current checkbox state on rebuild
    for (const m of dotMeshes) {
      m.visible = showPointsRef.current;
    }
    // Reverse dotMeshes so critical is checked first by raycaster
    dotMeshesRef.current = dotMeshes;
    // trendBarMeshesRef was populated during scene build above; no reset needed
    // (it's set fresh each rebuild since the scene is recreated)

    // Raycaster for tooltip hover detection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Build lookup map from source filename to file metadata
    const fileMetadata = {};
    if (data.files) {
      data.files.forEach(f => {
        fileMetadata[f.name] = f;
      });
    }
    fileMetadataRef.current = fileMetadata;

    const getPointDetails = (pointData) => {
      const fileMeta = fileMetadata[pointData._source] || {};
      const metricFps = getMetricFps(pointData);
      const metricConfig = getMetricConfig(pointData);
      // Calculate all FPS values
      const framerateFps = pointData.framerate || null;
      const gpuFps = pointData.gpuTime ? (1000 / pointData.gpuTime) : null;
      const cpuFps = pointData.cpuTime ? (1000 / pointData.cpuTime) : null;

      // Handle LOD cell data
      const isLODCell = pointData._isLODCell;
      const fpsStats = pointData._fpsStats;

      return {
        fps: metricFps?.toFixed(1) || 'N/A',
        metricLabel: metricConfig.label,
        metricUnit: metricConfig.unit,
        metricValue: metricConfig.displayValue || 'N/A',
        framerateFps: framerateFps?.toFixed(1) || null,
        gpuFps: gpuFps?.toFixed(1) || null,
        cpuFps: cpuFps?.toFixed(1) || null,
        device: pointData._device || fileMeta.deviceModel || 'Unknown',
        branch: fileMeta.branch || null,
        cl: fileMeta.cl || null,
        timestamp: fileMeta.timestamp || null,
        qualityLevel: pointData._quality || fileMeta.qualityLevel || null,
        awsRunUrl: fileMeta.awsRunUrl || null,
        source: pointData._source || null,
        // Store position/rotation for wireframe rendering
        position: pointData.position ? { ...pointData.position } : null,
        rotation: pointData.rotation ? { ...pointData.rotation } : null,
        // Rendering stats
        drawCalls: pointData.drawCalls ?? null,
        batches: pointData.batches ?? null,
        vertices: pointData.vertices ?? null,
        triangles: pointData.triangles ?? null,
        // LOD-specific data
        isLODCell,
        cellSize: pointData._cellSize ?? null,
        sampleCount: pointData._sampleCount ?? null,
        deviceCount: pointData._deviceCount ?? null,
        devices: pointData._devices ?? null,
        fpsMin: fpsStats?.min?.toFixed(1) ?? null,
        fpsMax: fpsStats?.max?.toFixed(1) ?? null,
        fpsAvg: fpsStats?.avg?.toFixed(1) ?? null,
        // Evaluator breakdown (when active)
        evaluatorResult: activeEvaluator ? evaluatePoint(pointData, activeEvaluator) : null,
        evaluatorName: activeEvaluator?.name || null,
        evaluatorMode: activeEvaluator?.mode || null,
        // Trend ring data (when hovering a trend ring)
        isTrendRing: pointData._isTrendRing || false,
        trendPeriodLabel: pointData._trendPeriodLabel || null,
        trendDelta: pointData._trendDelta ?? null,
        trendCurrentScore: pointData._trendCurrentScore ?? null,
        trendBaselineScore: pointData._trendBaselineScore ?? null,
        trendBaselineDate: pointData._trendBaselineDate || null
      };
    };

    const onMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(dotMeshesRef.current);

      if (intersects.length > 0) {
        const hit = intersects[0];
        const mesh = hit.object;
        const instanceId = hit.instanceId;
        const pointData = mesh.userData.points?.[instanceId];

        if (pointData) {
          const details = getPointDetails(pointData);
          setTooltip({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            fps: details.fps,
            metricLabel: details.metricLabel,
            metricUnit: details.metricUnit,
            metricValue: details.metricValue,
            device: details.device,
            // LOD-specific data
            isLODCell: details.isLODCell,
            sampleCount: details.sampleCount,
            deviceCount: details.deviceCount,
            fpsMin: details.fpsMin,
            fpsMax: details.fpsMax,
            // Evaluator breakdown
            evaluatorResult: details.evaluatorResult,
            evaluatorName: details.evaluatorName,
            evaluatorMode: details.evaluatorMode,
            // Trend ring data
            isTrendRing: details.isTrendRing,
            trendPeriodLabel: details.trendPeriodLabel,
            trendDelta: details.trendDelta,
            trendCurrentScore: details.trendCurrentScore,
            trendBaselineScore: details.trendBaselineScore,
            trendBaselineDate: details.trendBaselineDate,
          });

          // Position wireframe at the hovered point's frustum
          wireframeMesh.position.set(-pointData.position.x, pointData.position.y, pointData.position.z);
          if (pointData.rotation) {
            wireframeMesh.quaternion.set(
              pointData.rotation.x,
              pointData.rotation.y,
              pointData.rotation.z,
              pointData.rotation.w
            );
          } else {
            wireframeMesh.quaternion.identity();
          }
          wireframeMesh.visible = true;
        }
      } else {
        setTooltip(null);
        wireframeMesh.visible = false;
      }
    };

    // Track mousedown position to distinguish clicks from drags
    let mouseDownPos = { x: 0, y: 0 };
    const onMouseDown = (event) => {
      mouseDownPos = { x: event.clientX, y: event.clientY };
    };

    const onClick = (event) => {
      // Ignore if mouse moved more than 5px (it was a drag)
      const dx = event.clientX - mouseDownPos.x;
      const dy = event.clientY - mouseDownPos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) return;

      const rect = canvas.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(dotMeshesRef.current);

      if (intersects.length > 0) {
        const hit = intersects[0];
        const mesh = hit.object;
        const instanceId = hit.instanceId;
        const pointData = mesh.userData.points?.[instanceId];

        if (pointData) {
          // Create a unique key based on source and position
          const pointKey = `${pointData._source}:${pointData.position.x},${pointData.position.y},${pointData.position.z}`;

          setSelectedPoints(prev => {
            // Check if this point is already selected
            const existingIndex = prev.findIndex(p => p.pointKey === pointKey);
            if (existingIndex >= 0) {
              // Remove it
              return prev.filter((_, i) => i !== existingIndex);
            } else {
              // Add it
              const details = getPointDetails(pointData);
              details.id = nextPointIdRef.current++;
              details.pointKey = pointKey;
              return [...prev, details];
            }
          });
        }
      }
    };

    const onMouseLeave = () => {
      setTooltip(null);
      wireframeMesh.visible = false;
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('click', onClick);

    // Create float render target for frustum accumulation (R=badness sum, G=count)
    const frustumTarget = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
    });

    // Composite shader: divides accumulated badness by count, maps to color ramp
    const compositeScene = new THREE.Scene();
    const compositeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const compositeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        accumTex: { value: frustumTarget.texture },
        opacity: { value: showFrustumsRef.current ? 0.35 : 0.0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D accumTex;
        uniform float opacity;
        varying vec2 vUv;

        vec3 badnessToColor(float b) {
          // Linear three-zone ramp: green → yellow → red
          b = clamp(b, 0.0, 1.0);
          if (b <= 0.5) {
            // Green to Yellow (good to moderate)
            float t = b * 2.0;
            return vec3(t, 1.0, 0.0);
          }
          // Yellow to Red (moderate to bad)
          float t = (b - 0.5) * 2.0;
          return vec3(1.0, 1.0 - t, 0.0);
        }

        void main() {
          vec4 accum = texture2D(accumTex, vUv);
          float badnessSum = accum.r;
          float count = accum.g;

          if (count < 0.5) {
            discard; // No frustums here
          }

          float avgBadness = badnessSum / count;
          vec3 color = badnessToColor(avgBadness);

          // Coverage alpha: fade in gently from 1-2 frustums, full at 3+
          float coverageAlpha = smoothstep(0.0, 3.0, count);

          gl_FragColor = vec4(color, coverageAlpha * opacity);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
    compositeMaterialRef.current = compositeMaterial;
    const compositeQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      compositeMaterial
    );
    compositeScene.add(compositeQuad);

    // Add axis helper at origin corner for reference
    const axisSize = mapRange * 0.1;
    const axisHelper = new THREE.AxesHelper(axisSize);
    axisHelper.position.set(bounds.minX, bounds.minY, bounds.minZ);
    sceneContent.add(axisHelper);

    // Animation loop - render in layers
    let lastDotScale = 1;
    let lastReportedCameraState = null;  // Track last state reported via onCameraChange
    let lastCameraReportTime = 0;  // Throttle camera change reports
    function animate() {
      animationIdRef.current = requestAnimationFrame(animate);

      // Process WASD movement
      const speed = keys.shift ? moveSpeed * 3 : moveSpeed;
      moveDirection.set(0, 0, 0);

      // Get camera's actual forward direction (where you're looking)
      camera.getWorldDirection(cameraDirection);

      // Get camera's right direction (for A/D strafing)
      cameraRight.crossVectors(cameraDirection, camera.up).normalize();

      // Get screen-up direction projected onto map plane for W/S movement
      // This makes W/S move relative to current view rotation
      const screenUp = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
      const mapForward = new THREE.Vector3(screenUp.x, 0, screenUp.z);
      if (mapForward.lengthSq() < 0.001) {
        // Fallback if screen-up is vertical
        mapForward.set(cameraDirection.x, 0, cameraDirection.z);
      }
      mapForward.normalize();
      const mapRight = new THREE.Vector3(cameraRight.x, 0, cameraRight.z).normalize();

      // WASD for panning on map plane
      if (keys.w) moveDirection.add(mapForward);
      if (keys.s) moveDirection.sub(mapForward);
      if (keys.d) moveDirection.add(mapRight);
      if (keys.a) moveDirection.sub(mapRight);

      if (moveDirection.lengthSq() > 0) {
        moveDirection.normalize().multiplyScalar(speed);
        camera.position.add(moveDirection);
        controls.target.add(moveDirection);  // Move orbit target with camera
      }

      // Q/E for zoom (adjust orthographic frustum size)
      const zoomSpeed = keys.shift ? 0.05 : 0.02;
      if (keys.q) {
        // Zoom out
        camera.frustumSize *= (1 + zoomSpeed);
        const aspect = canvas.offsetWidth / canvas.offsetHeight;
        camera.left = -camera.frustumSize * aspect;
        camera.right = camera.frustumSize * aspect;
        camera.top = camera.frustumSize;
        camera.bottom = -camera.frustumSize;
        camera.updateProjectionMatrix();
      }
      if (keys.e) {
        // Zoom in
        camera.frustumSize *= (1 - zoomSpeed);
        const aspect = canvas.offsetWidth / canvas.offsetHeight;
        camera.left = -camera.frustumSize * aspect;
        camera.right = camera.frustumSize * aspect;
        camera.top = camera.frustumSize;
        camera.bottom = -camera.frustumSize;
        camera.updateProjectionMatrix();
      }

      controls.update();

      // Camera synchronization for compare mode
      const camState = syncCameraStateRef.current?.current;
      const currentViewerId = viewerIdRef.current;
      if (camState && currentViewerId) {
        const now = performance.now();

        // Check if another viewer updated more recently
        if (camState.lastUpdateSource !== currentViewerId && camState.lastUpdateTime > lastSyncTimeRef.current) {
          // Apply state from other viewer
          if (camState.position) {
            camera.position.copy(camState.position);
          }
          if (camState.target && controls.target) {
            controls.target.copy(camState.target);
          }
          if (camState.frustumSize != null) {
            camera.frustumSize = camState.frustumSize;
            const aspect = canvas.offsetWidth / canvas.offsetHeight;
            camera.left = -camera.frustumSize * aspect;
            camera.right = camera.frustumSize * aspect;
            camera.top = camera.frustumSize;
            camera.bottom = -camera.frustumSize;
          }
          if (camState.zoom != null) {
            camera.zoom = camState.zoom;
          }
          camera.updateProjectionMatrix();
          lastSyncTimeRef.current = camState.lastUpdateTime;
        } else {
          // Check if camera has changed (push our state to others)
          const posChanged = !camState.position ||
            Math.abs(camera.position.x - camState.position.x) > 0.01 ||
            Math.abs(camera.position.y - camState.position.y) > 0.01 ||
            Math.abs(camera.position.z - camState.position.z) > 0.01;
          const targetChanged = !camState.target ||
            Math.abs(controls.target.x - camState.target.x) > 0.01 ||
            Math.abs(controls.target.y - camState.target.y) > 0.01 ||
            Math.abs(controls.target.z - camState.target.z) > 0.01;
          const zoomChanged = Math.abs((camera.frustumSize || 0) - (camState.frustumSize || 0)) > 0.01 ||
            Math.abs((camera.zoom || 1) - (camState.zoom || 1)) > 0.001;

          if (posChanged || targetChanged || zoomChanged) {
            // Push this viewer's state
            camState.position = camera.position.clone();
            camState.target = controls.target.clone();
            camState.frustumSize = camera.frustumSize;
            camState.zoom = camera.zoom;
            camState.lastUpdateTime = now;
            camState.lastUpdateSource = currentViewerId;
          }
        }
      }

      // Report camera state changes to parent (throttled to avoid excessive updates)
      const now = performance.now();
      if (onCameraChangeRef.current && now - lastCameraReportTime > 100) {
        const currentState = {
          position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
          frustumSize: camera.frustumSize,
          zoom: camera.zoom
        };
        const stateChanged = !lastReportedCameraState ||
          Math.abs(currentState.position.x - lastReportedCameraState.position.x) > 0.1 ||
          Math.abs(currentState.position.z - lastReportedCameraState.position.z) > 0.1 ||
          Math.abs(currentState.frustumSize - lastReportedCameraState.frustumSize) > 0.1 ||
          Math.abs(currentState.zoom - lastReportedCameraState.zoom) > 0.01;
        if (stateChanged) {
          lastReportedCameraState = currentState;
          lastCameraReportTime = now;
          onCameraChangeRef.current(currentState);
        }
      }

      // Scale dot meshes based on effective viewport size (frustumSize / zoom)
      // This correctly responds to Q/E zoom (frustumSize) and scroll wheel (zoom)
      const effectiveSize = camera.frustumSize / camera.zoom;
      const zoomRatio = effectiveSize / viewSize;

      // Clamp scale factor to prevent dots from being too small or too large
      const maxDotScaleFactor = 15;  // Don't let dots scale more than 15x
      const dotScaleFactor = Math.min(maxDotScaleFactor, Math.max(zoomRatio, minDotScaleFactor));
      if (dotScaleFactor !== lastDotScale) {
        lastDotScale = dotScaleFactor;
        const scaledDotScale = new THREE.Vector3(dotScaleFactor, dotScaleFactor, dotScaleFactor);
        dotMeshes.forEach(mesh => {
          const pts = mesh.userData.points;
          if (!pts) return;
          pts.forEach((p, i) => {
            tempPosition.set(-p.position.x, p.position.y, p.position.z);
            if (p.rotation) {
              tempQuaternion.set(p.rotation.x, p.rotation.y, p.rotation.z, p.rotation.w);
            } else {
              tempQuaternion.identity();
            }
            tempMatrix.compose(tempPosition, tempQuaternion, scaledDotScale);
            mesh.setMatrixAt(i, tempMatrix);
          });
          mesh.instanceMatrix.needsUpdate = true;
        });
        // Trend bar meshes are in dotMeshes, so they get scaled above automatically.
      }

      // LOD level detection - use zoomRatio for consistency with dot scaling
      // This ensures LOD changes happen at the same zoom levels that affect dot visibility
      // Uses point budget system to optimize LOD selection based on current point count
      if (onLODChangeRef.current) {
        const currentPointCount = points.length;
        // Pass the actual lodCacheRef (not the wrapper ref) so calculateOptimalLOD can access .current.get()
        const { level: newLODLevel, reason } = calculateOptimalLOD(zoomRatio, currentPointCount, lodCacheRefCurrent.current);
        if (newLODLevel !== lastLODRef.current) {
          lastLODRef.current = newLODLevel;
          const newLOD = LOD_LEVELS[newLODLevel];

          // Calculate viewport bounds for full resolution queries
          // Convert camera frustum to world coordinates
          const aspect = canvas.offsetWidth / canvas.offsetHeight;
          const effectiveSize = camera.frustumSize / camera.zoom;
          const halfWidth = effectiveSize * aspect;
          const halfHeight = effectiveSize;
          const camX = camera.position.x;
          const camZ = camera.position.z;

          // Note: Scene is rotated 180° around Y, so both X and Z are negated in display
          // Convert back to Unity/BigQuery world coordinates by negating both
          const viewport = {
            minX: -(camX + halfWidth),
            maxX: -(camX - halfWidth),
            minZ: -(camZ + halfHeight),
            maxZ: -(camZ - halfHeight)
          };

          console.log(`[HeatmapViewer] LOD change detected: ${newLODLevel} (${newLOD.label}), zoomRatio=${zoomRatio.toFixed(2)}, reason=${reason}, points=${currentPointCount}`);
          onLODChangeRef.current(newLODLevel, viewport);
        }
      }

      // 1. Render frustums to offscreen accumulation target
      renderer.setRenderTarget(frustumTarget);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      renderer.render(frustumScene, camera);
      renderer.setRenderTarget(null);

      // 2. Render main scene (map, dots, wedges)
      renderer.render(scene, camera);

      // 3. Composite frustum heatmap on top
      renderer.autoClear = false;
      renderer.render(compositeScene, compositeCamera);
      renderer.autoClear = true;
    }
    animate();

    // Handle resize
    function handleResize() {
      // Get dimensions from parent container to handle fullscreen properly
      const container = canvas.parentElement;
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      if (newWidth === 0 || newHeight === 0) return;

      const newAspect = newWidth / newHeight;
      const fs = camera.frustumSize;
      camera.left = -fs * newAspect;
      camera.right = fs * newAspect;
      camera.top = fs;
      camera.bottom = -fs;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
      frustumTarget.setSize(newWidth, newHeight);
    }

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      // Save camera state before cleanup so it can be restored on data updates (LOD changes)
      if (cameraRef.current) {
        savedCameraPosition.current = cameraRef.current.position.clone();
        savedFrustumSize.current = cameraRef.current.frustumSize;
        savedCameraZoom.current = cameraRef.current.zoom;
      }
      if (controlsRef.current) {
        savedControlsTarget.current = controlsRef.current.target.clone();
      }

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', keyCleanup.onKeyDown);
      window.removeEventListener('keyup', keyCleanup.onKeyUp);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('click', onClick);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      controls.dispose();
      renderer.dispose();
      coneGeometry.dispose();
      wireframeGeometry.dispose();
      wireframeMaterial.dispose();
      // Dispose all dot/wedge meshes and their geometries/materials
      dotMeshesRef.current.forEach(mesh => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) mesh.material.dispose();
      });
      frustumAccumMaterial.dispose();
      frustumTarget.dispose();
      compositeMaterial.dispose();
      compositeMaterialRef.current = null;
      dotMeshesRef.current = [];
      setTooltip(null);
      setSelectedPoints([]);
    };
  }, [data, mapConfig, colorMetric, getMetricFps, getMetricBadness, getMetricConfig, sharedBounds, trendData, trendPeriod]);

  // Update selected point wireframes when selectedPoints changes
  useEffect(() => {
    const { group, geometry, material } = selectedWireframesRef.current;
    if (!group || !geometry || !material) return;

    // Clear existing wireframes
    while (group.children.length > 0) {
      group.remove(group.children[0]);
    }

    // Add wireframe for each selected point
    selectedPoints.forEach(point => {
      if (!point.position) return;
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(-point.position.x, point.position.y, point.position.z);
      if (point.rotation) {
        mesh.quaternion.set(
          point.rotation.x,
          point.rotation.y,
          point.rotation.z,
          point.rotation.w
        );
      }
      mesh.renderOrder = 99;
      group.add(mesh);
    });
  }, [selectedPoints]);

  // Toggle dot/frustum/trend bar visibility based on props
  useEffect(() => {
    for (const mesh of dotMeshesRef.current) {
      if (mesh.userData._isTrendBar) {
        mesh.visible = trendPeriod !== 'none';
      } else {
        mesh.visible = showPoints;
      }
    }
    for (const mesh of trendBarMeshesRef.current) {
      mesh.visible = trendPeriod !== 'none';
    }
    if (compositeMaterialRef.current) {
      compositeMaterialRef.current.uniforms.opacity.value = showFrustums ? 0.35 : 0;
    }
  }, [showPoints, showFrustums, trendPeriod]);

  const hasPoints = data?.capturePoints && data.capturePoints.length > 0;
  if (!hasPoints && !mapConfig) {
    return (
      <div className="viewer-empty">
        {externalLoading ? (
          <>
            <div className="loading-spinner small"></div>
            <p>Loading scan data...</p>
          </>
        ) : (
          <p>No capture points available</p>
        )}
      </div>
    );
  }

  return (
    <div className={`heatmap-viewer ${isFullscreen ? 'fullscreen' : ''}`} ref={viewerContainerRef}>
      {stats && (
        <div className="viewer-stats">
          <span className="stat">
            <strong>{stats.metricLabel ? `Avg ${stats.metricLabel}:` : 'Avg FPS:'}</strong> {stats.avgFps}
          </span>
          <span className="stat">
            <strong>Min:</strong> {stats.minFps}
          </span>
          <span className="stat">
            <strong>Max:</strong> {stats.maxFps}
          </span>
          <span className="stat">
            <strong>Points:</strong> {stats.pointCount.toLocaleString()}
            {stats.cellSize && stats.totalSamples && stats.totalSamples !== stats.pointCount && (
              <> from {stats.totalSamples.toLocaleString()} samples ({stats.cellSize}m cells)</>
            )}
          </span>
        </div>
      )}
      <div className="viewer-container-inner">
        <canvas ref={canvasRef} className="viewer-canvas" />
        {/* LOD Cache indicator - top right */}
        {lodProgress && Object.keys(lodProgress).length > 0 && (
          <div className="viewer-lod-cache">
            <span className="lod-cache-label">LOD:</span>
            {[0, 1, 2].map(level => {
              const status = lodProgress[level] || 'pending';
              const isActive = currentLOD === level;
              return (
                <span
                  key={level}
                  className={`lod-cache-indicator lod-${status} ${isActive ? 'lod-active' : ''}`}
                  title={`LOD ${level}: ${status}${isActive ? ' (active)' : ''}`}
                >
                  {level}
                </span>
              );
            })}
          </div>
        )}
        {externalLoading && (
          <div className="viewer-loading-overlay">
            <div className="loading-spinner small"></div>
            <span>Loading more scan data...</span>
          </div>
        )}
        <button
          className="fullscreen-toggle"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
        >
          {isFullscreen ? '⤓' : '⤢'}
        </button>
        {(() => {
          const config = getMetricConfig();
          const ticks = config.ticks;
          const reversed = config.lowerIsBetter;
          return (
            <div className={`viewer-legend ${reversed ? 'legend-reversed' : ''}`}>
              <div className="legend-gradient"></div>
              <div className="legend-ticks">
                <span className="legend-tick" style={{left: '0%'}}>{ticks[0]}</span>
                <span className="legend-tick" style={{left: '33%'}}>{ticks[1]}</span>
                <span className="legend-tick" style={{left: '66%'}}>{ticks[2]}</span>
                <span className="legend-tick" style={{left: '100%'}}>{ticks[3]}</span>
              </div>
              <div className="legend-unit">{config.unit}</div>
            </div>
          );
        })()}
        {tooltip && (
          <div
            className="viewer-tooltip"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            {!tooltip.isTrendRing && (
              <>
                <div className="tooltip-fps">{tooltip.metricValue}{tooltip.metricUnit && !String(tooltip.metricValue).includes('%') ? ' ' + tooltip.metricUnit : ''}</div>
                <div className="tooltip-device">{tooltip.device}</div>
              </>
            )}
            {tooltip.isLODCell && !tooltip.isTrendRing && (
              <div className="tooltip-lod-info">
                <div className="tooltip-samples">{tooltip.sampleCount?.toLocaleString()} samples</div>
                {tooltip.deviceCount > 1 && (
                  <div className="tooltip-devices">{tooltip.deviceCount} devices</div>
                )}
                {tooltip.fpsMin && tooltip.fpsMax && tooltip.fpsMin !== tooltip.fpsMax && (
                  <div className="tooltip-fps-range">Range: {tooltip.fpsMin} - {tooltip.fpsMax} FPS</div>
                )}
              </div>
            )}
            {tooltip.evaluatorResult && !tooltip.isTrendRing && (
              <div className="tooltip-evaluator">
                <div className="tooltip-eval-header">
                  {tooltip.evaluatorName}
                  <span className={`tooltip-eval-badge ${tooltip.evaluatorResult.exceeded ? 'exceeded' : 'ok'}`}>
                    {tooltip.evaluatorResult.exceeded ? 'OVER' : 'OK'}
                  </span>
                </div>
                <div className="tooltip-eval-rules">
                  {tooltip.evaluatorResult.ruleResults.map((r, i) => {
                    const info = AVAILABLE_METRICS.find(m => m.key === r.metric);
                    const pct = Math.min(r.severity * 100, 150);
                    return (
                      <div key={i} className="tooltip-eval-rule">
                        <div className="tooltip-eval-rule-label">
                          <span>{info?.label || r.metric}</span>
                          <span className="tooltip-eval-rule-value">
                            {r.value != null ? (r.value >= 1000 ? (r.value / 1000).toFixed(0) + 'k' : r.value) : '—'}
                            <span className="tooltip-eval-rule-threshold"> / {r.threshold >= 1000 ? (r.threshold / 1000).toFixed(0) + 'k' : r.threshold}</span>
                          </span>
                        </div>
                        <div className="tooltip-eval-bar-bg">
                          <div
                            className={`tooltip-eval-bar ${r.exceeded ? 'exceeded' : ''}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {tooltip.isTrendRing && (
              <div className="tooltip-trend-ring">
                <div className="tooltip-trend-header">{tooltip.trendPeriodLabel}</div>
                <div className={`tooltip-trend-delta ${tooltip.trendDelta > 0.05 ? 'worsened' : tooltip.trendDelta < -0.05 ? 'improved' : 'stable'}`}>
                  {tooltip.trendDelta > 0.05 ? '▲' : tooltip.trendDelta < -0.05 ? '▼' : '—'}
                  {' '}
                  {tooltip.trendDelta != null ? (tooltip.trendDelta > 0 ? '+' : '') + (tooltip.trendDelta * 100).toFixed(0) + '%' : '—'}
                </div>
                <div className="tooltip-trend-scores">
                  <span>Now: {tooltip.trendCurrentScore != null ? (tooltip.trendCurrentScore * 100).toFixed(0) + '%' : '—'}</span>
                  <span>Then: {tooltip.trendBaselineScore != null ? (tooltip.trendBaselineScore * 100).toFixed(0) + '%' : '—'}</span>
                </div>
                {tooltip.trendBaselineDate && (
                  <div className="tooltip-trend-date">
                    {new Date(tooltip.trendBaselineDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {selectedPoints.length > 0 && (
          <div className="viewer-detail-panels">
            {selectedPoints.map((point, index) => (
              <div key={point.id} className="viewer-detail-panel">
                <button
                  className="detail-close"
                  onClick={() => setSelectedPoints(prev => prev.filter(p => p.id !== point.id))}
                >×</button>
                <div className="detail-row detail-fps-grid">
                  {point.framerateFps && (
                    <div className="detail-fps-item">
                      <span className="detail-fps-value">{point.framerateFps}</span>
                      <span className="detail-fps-label">FPS</span>
                    </div>
                  )}
                  {point.gpuFps && (
                    <div className="detail-fps-item">
                      <span className="detail-fps-value">{point.gpuFps}</span>
                      <span className="detail-fps-label">FPS (GPU)</span>
                    </div>
                  )}
                  {point.cpuFps && (
                    <div className="detail-fps-item">
                      <span className="detail-fps-value">{point.cpuFps}</span>
                      <span className="detail-fps-label">FPS (CPU)</span>
                    </div>
                  )}
                </div>
                {(point.drawCalls != null || point.batches != null || point.vertices != null || point.triangles != null) && (
                  <div className="detail-row detail-render-grid">
                    {point.drawCalls != null && (
                      <div className="detail-render-item">
                        <span className="detail-render-value">{point.drawCalls.toLocaleString()}</span>
                        <span className="detail-render-label">Draws</span>
                      </div>
                    )}
                    {point.batches != null && (
                      <div className="detail-render-item">
                        <span className="detail-render-value">{point.batches.toLocaleString()}</span>
                        <span className="detail-render-label">Batches</span>
                      </div>
                    )}
                    {point.vertices != null && (
                      <div className="detail-render-item">
                        <span className="detail-render-value">{(point.vertices / 1000).toFixed(0)}k</span>
                        <span className="detail-render-label">Verts</span>
                      </div>
                    )}
                    {point.triangles != null && (
                      <div className="detail-render-item">
                        <span className="detail-render-value">{(point.triangles / 1000).toFixed(0)}k</span>
                        <span className="detail-render-label">Tris</span>
                      </div>
                    )}
                  </div>
                )}
                {point.evaluatorResult && (
                  <div className="detail-row detail-evaluator">
                    <div className="detail-eval-header">
                      <span className="detail-eval-name">{point.evaluatorName}</span>
                      <span className={`detail-eval-badge ${point.evaluatorResult.exceeded ? 'exceeded' : 'ok'}`}>
                        {point.evaluatorResult.exceeded ? 'OVER' : 'OK'}
                      </span>
                      <span className="detail-eval-score">{point.evaluatorResult.score != null ? (point.evaluatorResult.score * 100).toFixed(0) + '%' : '—'}</span>
                    </div>
                    {point.evaluatorResult.ruleResults.map((r, i) => {
                      const info = AVAILABLE_METRICS.find(m => m.key === r.metric);
                      const pct = Math.min(r.severity * 100, 150);
                      return (
                        <div key={i} className="detail-eval-rule">
                          <div className="detail-eval-rule-label">
                            <span>{info?.label || r.metric}</span>
                            <span>{r.value != null ? r.value.toLocaleString() : '—'} / {r.threshold.toLocaleString()}</span>
                          </div>
                          <div className="tooltip-eval-bar-bg">
                            <div
                              className={`tooltip-eval-bar ${r.exceeded ? 'exceeded' : ''}`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="detail-row detail-compact">
                  {point.device}{point.qualityLevel && point.qualityLevel !== 'Unknown' ? ` (${point.qualityLevel})` : ''}
                </div>
                {(point.branch || point.cl) && (
                  <div className="detail-row detail-compact">
                    {point.branch}{point.branch && point.cl ? ' - ' : ''}{point.cl}
                  </div>
                )}
                {point.timestamp && (
                  <div className="detail-row detail-compact detail-muted">
                    {(() => {
                      const ts = parseTimestamp(point.timestamp);
                      return ts ? ts.toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) : point.timestamp;
                    })()}
                  </div>
                )}
                {(point.awsRunUrl || (unity && point.position)) && (
                  <div className="detail-row detail-links">
                    {point.awsRunUrl && (
                      <a href={point.awsRunUrl} target="_blank" rel="noopener noreferrer" className="detail-link detail-link-aws">AWS</a>
                    )}
                    {unity && point.position && (
                      unity.connected ? (
                        <button
                          className="detail-link detail-link-unity"
                          onClick={() => unity.jumpToPoint(
                            point.position.x, point.position.y, point.position.z,
                            point.rotation ? Math.atan2(2 * (point.rotation.w * point.rotation.y), 1 - 2 * (point.rotation.y * point.rotation.y)) * 180 / Math.PI : 0,
                            zoneId
                          )}
                        >
                          Jump in Unity
                        </button>
                      ) : (
                        <button
                          className="detail-link detail-link-unity detail-link-disconnected"
                          onClick={() => unity.openBridge()}
                          title="Connect to Unity Editor"
                        >
                          Connect Unity
                        </button>
                      )
                    )}
                  </div>
                )}
                {point.source && (
                  <div className="detail-row detail-compact detail-muted detail-source">{point.source}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="viewer-note">
        WASD or left-drag to pan | Q/E or scroll to zoom | Right-drag to spin | Shift for speed
      </p>
    </div>
  );
}

function Heatmaps({ api, filters, onFiltersChange, links, initialArea }) {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [stfZones, setStfZones] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const unity = useUnityBridge();

  // Filter state is lifted to App.js to persist across tab switches
  const { daysFilter, customStartDate, customEndDate, colorMetric, selectedDevices, selectedQualities, selectedBranches, selectedCLs } = filters;
  // For custom date range, pass startDate/endDate; for presets, pass null
  const startDate = daysFilter === 'custom' && customStartDate ? customStartDate : null;
  const endDate = daysFilter === 'custom' && customEndDate ? customEndDate : null;
  // Effective days for API calls (0 when custom since dates take precedence)
  const effectiveDays = daysFilter === 'custom' ? 0 : daysFilter;

  // Auto-select the first evaluator for area coloring when evaluators exist
  // and the user hasn't manually chosen a different metric
  useEffect(() => {
    if (evaluators.length > 0 && colorMetric === 'framerate') {
      updateFilter('colorMetric', `eval:${evaluators[0].id}`);
    }
  }, [evaluators]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the active evaluator object when colorMetric is 'eval:<id>'
  const activeEvaluator = useMemo(() => {
    if (!colorMetric?.startsWith('eval:')) return null;
    const id = colorMetric.slice(5);
    return evaluators.find(e => e.id === id) || null;
  }, [colorMetric, evaluators]);

  // Helper to update a single filter value
  const updateFilter = useCallback((key, value) => {
    onFiltersChange(prev => ({ ...prev, [key]: value }));
  }, [onFiltersChange]);

  // Filter options (available values) - these can be local since they're derived from API
  const [filterOptions, setFilterOptions] = useState({ devices: [], qualityLevels: [], branches: [], cls: [] });

  // Fetch filter options when days filter or data source changes
  useEffect(() => {
    if (daysFilter === 'custom' && !customStartDate) return;
    async function fetchFilters() {
      try {
        const result = await api.getBigQueryHeatmapGlobalFilters(effectiveDays, startDate, endDate);
        setFilterOptions({
          devices: result.devices || [],
          qualityLevels: result.qualities || [],
          branches: result.branches || [],
          cls: result.cls || []
        });
      } catch (err) {
        console.warn('Failed to load filter options:', err.message);
      }
    }
    fetchFilters();
  }, [api, effectiveDays, startDate, endDate]);

  useEffect(() => {
    if (daysFilter === 'custom' && !customStartDate) return;
    async function fetchAreas() {
      setLoading(true);
      try {
        const [areaResult, stfResult, evalResult] = await Promise.all([
          api.getBigQueryHeatmapAreas(effectiveDays, startDate, endDate),
          api.getSTFScanZones(effectiveDays === 0 ? 30 : effectiveDays, startDate, endDate).catch(() => ({ zones: [] })),
          api.getEvaluators().catch(() => ({ evaluators: [] })),
        ]);
        setAreas(areaResult.areas || []);
        setStfZones(stfResult.zones || []);
        setEvaluators(evalResult.evaluators || []);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchAreas();
  }, [api, effectiveDays, startDate, endDate]);

  // Fetch performance summary
  useEffect(() => {
    async function fetchPerformanceSummary() {
      try {
        // Pass null if no branch selected to get data from all branches
        const branch = selectedBranches.length > 0 ? selectedBranches[0] : null;
        const summaryFilters = {
          cls: selectedCLs,
          devices: selectedDevices,
          qualities: selectedQualities
        };
        const result = await api.getPerformanceSummary(effectiveDays, branch, summaryFilters, startDate, endDate);
        setPerformanceSummary(result.data || []);
      } catch (err) {
        console.warn('Failed to load performance summary:', err.message);
        setPerformanceSummary(null);
      }
    }

    if (daysFilter === 'custom' && !customStartDate) return;
    fetchPerformanceSummary();
  }, [api, effectiveDays, startDate, endDate, selectedBranches, selectedCLs, selectedDevices, selectedQualities]);

  // Combined filters object to pass to AreaSection
  const activeFilters = {
    devices: selectedDevices,
    qualities: selectedQualities,
    branches: selectedBranches,
    cls: selectedCLs
  };

  // Build a map of STF zone data keyed by bare zone name
  const stfByBareKey = useMemo(() => {
    const map = new Map();
    for (const z of stfZones) {
      map.set(bareZoneKey(z.zone_name), z);
    }
    return map;
  }, [stfZones]);

  // Merge areas with STF data: attach stfZone to matching areas, add STF-only zones
  const mergedAreas = useMemo(() => {
    const result = areas.map(area => {
      const key = bareZoneKey(area.zoneName || area.name);
      const stfZone = stfByBareKey.get(key);
      return { ...area, stfZone: stfZone || null };
    });

    // Add STF-only zones (no matching area)
    const areaKeys = new Set(areas.map(a => bareZoneKey(a.zoneName || a.name)));
    for (const z of stfZones) {
      const key = bareZoneKey(z.zone_name);
      if (!areaKeys.has(key)) {
        result.push({
          name: stfZoneToMapName(z.zone_name),
          zoneName: stfZoneToMapName(z.zone_name),
          fileCount: 0,
          runCount: 0,
          stfZone: z,
          _stfOnly: true,
        });
      }
    }

    return result;
  }, [areas, stfZones, stfByBareKey]);

  if (loading) {
    return (
      <div className="heatmaps">
        <div className="heatmaps-loading">
          <div className="loading-spinner"></div>
          <p>Loading heatmap areas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="heatmaps">
        <div className="heatmaps-error">
          <h3>Failed to load heatmap data</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (areas.length === 0 && stfZones.length === 0) {
    return (
      <div className="heatmaps">
        <div className="heatmaps-controls" style={{ marginBottom: '20px' }}>
          <div className="heatmaps-filters">
            <div className="heatmaps-filters-row">
              <div className="heatmaps-filter">
                <label htmlFor="date-range">Date range:</label>
                <select
                  id="date-range"
                  value={daysFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateFilter('daysFilter', val === 'custom' ? 'custom' : Number(val));
                  }}
                >
                  {DATE_RANGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {daysFilter === 'custom' && (
                  <div className="custom-date-inputs">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => updateFilter('customStartDate', e.target.value)}
                    />
                    <span className="date-separator">to</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => updateFilter('customEndDate', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="heatmaps-empty">
          <h3>No area data available</h3>
          <p>Run performance tests to generate area data for visualization.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="heatmaps">
      <div className="heatmaps-header">
        <h2>Areas</h2>
        <div className="heatmaps-unity-status">
          {unity.connected ? (
            <span className="unity-indicator unity-connected" title="Connected to Unity Editor via LiveLink">
              Unity Connected
            </span>
          ) : (
            <button className="unity-indicator unity-disconnected" onClick={unity.openBridge} title="Open Unity LiveLink bridge">
              Connect Unity
            </button>
          )}
        </div>
        <p className="heatmaps-description">
          Performance data and heatmaps for each game area.
          Click an area to view performance analysis and heatmap visualization.
          {links?.harvestHealth && (
            <>
              {' '}
              <a
                href={`${links.harvestHealth}/scorecard?days=${effectiveDays || 7}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cross-link-pill"
              >
                Scorecard overview ↗
              </a>
            </>
          )}
        </p>
      </div>

      <div className="heatmaps-controls">
        <div className="heatmaps-summary">
          <span>{mergedAreas.length} area{mergedAreas.length !== 1 ? 's' : ''} with data</span>
        </div>
        <div className="heatmaps-filters">
          <div className="heatmaps-filters-row">
            <div className="heatmaps-filter">
              <label htmlFor="date-range">Date range:</label>
              <select
                id="date-range"
                value={daysFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  updateFilter('daysFilter', val === 'custom' ? 'custom' : Number(val));
                }}
              >
                {DATE_RANGE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {daysFilter === 'custom' && (
                <div className="custom-date-inputs">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => updateFilter('customStartDate', e.target.value)}
                  />
                  <span className="date-separator">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => updateFilter('customEndDate', e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="heatmaps-filter">
              <label htmlFor="color-metric">Color by:</label>
              <select
                id="color-metric"
                value={colorMetric}
                onChange={(e) => updateFilter('colorMetric', e.target.value)}
              >
                <option value="framerate">Framerate (FPS)</option>
                <option value="gpuFps">GPU (as FPS)</option>
                <option value="cpuFps">CPU (as FPS)</option>
                <option disabled>── Geometry ──</option>
                <option value="triangles">Triangles</option>
                <option value="vertices">Vertices</option>
                <option value="visibleRenderers">Visible Renderers</option>
                <option value="candidateRenderers">Renderer Density</option>
                <option value="candidateTriangles">Triangle Density</option>
                <option value="cullingRatio">Culling Ratio</option>
                <option disabled>── Draw Calls ──</option>
                <option value="drawCalls">Draw Calls</option>
                <option value="batches">Batches</option>
                <option value="setPassCalls">SetPass Calls</option>
                <option value="opaqueDraws">Opaque Draws</option>
                <option value="transparentDraws">Transparent Draws</option>
                <option value="depthPrepassDraws">Depth Prepass Draws</option>
                <option disabled>── Shadows & Lights ──</option>
                <option value="mainLightShadowDraws">Main Light Shadow Draws</option>
                <option value="additionalLightShadowDraws">Additional Light Shadow Draws</option>
                <option value="shadowCasterRenderers">Shadow Casters</option>
                <option value="candidateLights">Lights in View</option>
                <option value="candidateShadowCastingLights">Shadow-Casting Lights</option>
                <option disabled>── Batching ──</option>
                <option value="batchedDraws">Batched Draws</option>
                <option value="unbatchedDraws">Unbatched Draws</option>
                <option value="distinctShaderVariants">Shader Variants</option>
                {evaluators.length > 0 && <option disabled>── Evaluators ──</option>}
                {evaluators.map(ev => (
                  <option key={ev.id} value={`eval:${ev.id}`}>{ev.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="heatmaps-filters-row">
            <MultiSelect
              label="Devices:"
              options={filterOptions.devices}
              selected={selectedDevices}
              onChange={(v) => updateFilter('selectedDevices', v)}
              placeholder="All devices"
            />
            <MultiSelect
              label="Quality:"
              options={filterOptions.qualityLevels}
              selected={selectedQualities}
              onChange={(v) => updateFilter('selectedQualities', v)}
              placeholder="All qualities"
            />
            <MultiSelect
              label="Branch:"
              options={filterOptions.branches}
              selected={selectedBranches}
              onChange={(v) => updateFilter('selectedBranches', v)}
              placeholder="All branches"
            />
            <MultiSelect
              label="CL:"
              options={filterOptions.cls}
              selected={selectedCLs}
              onChange={(v) => updateFilter('selectedCLs', v)}
              placeholder="All CLs"
            />
          </div>
        </div>
      </div>

      <div className="areas-list">
        {mergedAreas.map(area => (
          <AreaSection
            key={area.name}
            area={area}
            api={api}
            daysFilter={effectiveDays}
            startDate={startDate}
            endDate={endDate}
            filters={activeFilters}
            colorMetric={colorMetric}
            activeEvaluator={activeEvaluator}
            performanceSummary={performanceSummary}
            branch={selectedBranches.length > 0 ? selectedBranches[0] : null}
            onCLFilter={(cls) => updateFilter('selectedCLs', cls)}
            defaultExpanded={initialArea ? matchesArea(area.name, initialArea) : false}
            links={links}
            unity={unity}
          />
        ))}
      </div>
    </div>
  );
}

export { HeatmapViewer };
export default Heatmaps;
